import { type FormEvent, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import {
  AGENT_MODELS,
  type AddressingStyle,
  type AgentModel,
  ApiError,
  EFFORT_LEVELS,
  type EffortLevel,
  describeFormatPreset,
  fetchClaudeState,
  fetchContentList,
  fetchPresets,
  fetchProviders,
  fetchVideoModels,
  type FormatPreset,
  type ProviderCredits,
  type ProviderUpdate,
  type ProviderValidation,
  type ProviderView,
  saveProvider,
  validateProvider,
  type StorageCheckResult,
  type StorageSettingsUpdate,
  type StorageTestResult,
  type StorageView,
  type ToneOption,
  fetchSettings,
  fetchStorage,
  fetchMeta,
  fetchAdsAgentSettings,
  fetchRepositories,
  type AdsAgentSettings,
  type AdsAgentSettingsUpdate,
  type AdsAgentSettingsView,
  type Repository,
  saveAdsAgentSettings,
  type MetaCheckResult,
  type MetaConnectionStatus,
  type MetaConnectionTestResult,
  type MetaSettings,
  type MetaSettingsUpdate,
  removeMetaToken,
  saveMeta,
  saveSettings,
  type Settings,
  saveStorage,
  startClaudeLogin,
  stopClaudeLogin,
  type TtsProvider,
  type VideoCatalogueView,
  type VideoModelListing,
  testMetaConnection,
  testStorage,
  toneLabel,
  languageLabel,
} from '../api.ts';
import { describeError, DESKTOP_QUERY, useAppData, useMediaQuery } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { formatMoney, minorFactor } from '../adsMoney.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { GuidelinesPanel } from '../GuidelinesPanel.tsx';
import { RulesPanel } from '../RulesPanel.tsx';
import type { TerminalPaneHandle } from '../TerminalPane.tsx';
import { localTime } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import {
  Badge,
  Facts,
  Gauge,
  gigabytes,
  Notice,
  PageHeader,
  Panel,
  setAllPanels,
  Skeleton,
} from '../ui.tsx';

// xterm.js only matters once an operator actually signs Claude in.
const TerminalPane = lazy(() =>
  import('../TerminalPane.tsx').then((module) => ({ default: module.TerminalPane })),
);

/**
 * Settings. **Claude Code**, **Storage**, **Meta Ads**, **Providers**, **Video generation**,
 * **Spend**, **Voice**, **Production**, **Defaults**, **Format presets and
 * tones**, **Guidelines** (the ad bible) and **Models** — the model each stage runs on.
 *
 * Every section is a collapsed row with its state in one line until it is
 * opened, so the page reads as an overview: the summary comes from what is
 * saved, and a section that needs action carries a `danger` or `wait` badge
 * while folded. Each remembers whether it was left open, **Expand all** and
 * **Collapse all** in the header set every one at once, and a link to
 * `/settings#<id>` lands on that section open.
 *
 * The storage panel is here first because nothing can be *delivered* before
 * there is somewhere to put it, and because the operator's real question is
 * not "did it save" but "does it work" — which is what **Test connection**
 * answers, one check per thing that can be wrong.
 */
export function Settings() {
  return (
    <div className="page">
      <PageHeader
        title="Settings"
        subtitle="Where finished work is delivered, and how this install talks to the rest of it."
        actions={
          <>
            <button
              type="button"
              className="button button--small"
              onClick={() => {
                setAllPanels(true);
              }}
            >
              <Icon name="chevron-down" />
              Expand all
            </button>
            <button
              type="button"
              className="button button--small"
              onClick={() => {
                setAllPanels(false);
              }}
            >
              <Icon name="chevron-right" />
              Collapse all
            </button>
        </>
        }
      />
      <ClaudePanel />
      <StoragePanel />
      <MetaAdsPanel />
      <ProvidersPanel />
      <VideoPanel />
      <SpendPanel />
      <VoicePanel />
      <ProductionPanel />
      <DefaultsPanel />
      <RulesPanel />
      <GuidelinesPanel />
      <ModelsPanel />
    </div>
  );
}

/** A section's summary before its data has arrived, or when it could not. */
function pendingSummary(error: string | null): string {
  return error === null ? 'loading…' : 'could not load';
}

/** The stages whose model the operator picks, in the order they run. */
const MODEL_STAGES: readonly {
  key: 'ideasModel' | 'planningModel' | 'productionModel' | 'reviewModel';
  label: string;
  hint: string;
}[] = [
  {
    key: 'ideasModel',
    label: 'Ideas',
    hint: 'Works your ideas out into drafts',
  },
  {
    key: 'planningModel',
    label: 'Planning',
    hint: 'Planning writes the plan with you in the browser terminal.',
  },
  {
    key: 'productionModel',
    label: 'Production',
    hint: 'Production runs every step of the plan, from script to render.',
  },
  {
    key: 'reviewModel',
    label: 'Review',
    hint: 'Review grades a version before you see it.',
  },
];

/**
 * The stages whose `--effort` the operator picks (synthesis O3). The built-in
 * levels put the thinking where the ad is decided — planning, the composition,
 * the critic and the review — and leave the mechanical steps to the CLI.
 */
const EFFORT_STAGES: readonly {
  key:
    | 'ideasEffort'
    | 'planningEffort'
    | 'composeEffort'
    | 'productionEffort'
    | 'reviewEffort'
    | 'critiqueEffort';
  label: string;
  hint: string;
}[] = [
  { key: 'ideasEffort', label: 'Ideas', hint: 'Works your ideas out into drafts. Built-in: medium.' },
  { key: 'planningEffort', label: 'Planning', hint: 'Built-in: high.' },
  { key: 'composeEffort', label: 'Compose', hint: 'The step that builds the picture. Built-in: xhigh.' },
  {
    key: 'productionEffort',
    label: 'Other steps',
    hint: 'Capture, render, captions and the rest. Built-in: Claude Code chooses.',
  },
  { key: 'reviewEffort', label: 'Review', hint: 'Built-in: high.' },
  { key: 'critiqueEffort', label: 'Critic', hint: 'The cold read of a script or a draft. Built-in: high.' },
];

/** The form's reading of the models and efforts: `''` is "let Claude Code choose". */
type ModelsForm = Record<(typeof MODEL_STAGES)[number]['key'], AgentModel | ''> &
  Record<(typeof EFFORT_STAGES)[number]['key'], EffortLevel | ''>;

function modelsFormOf(settings: Settings): ModelsForm {
  return {
    ideasModel: settings.ideasModel ?? '',
    planningModel: settings.planningModel ?? '',
    productionModel: settings.productionModel ?? '',
    reviewModel: settings.reviewModel ?? '',
    ideasEffort: settings.ideasEffort ?? '',
    planningEffort: settings.planningEffort ?? '',
    composeEffort: settings.composeEffort ?? '',
    productionEffort: settings.productionEffort ?? '',
    reviewEffort: settings.reviewEffort ?? '',
    critiqueEffort: settings.critiqueEffort ?? '',
  };
}

/**
 * **Models**: the model each stage runs on. The server reads these rows on
 * every step, so a saved choice applies to the next step without a restart.
 */
function ModelsPanel() {
  const toast = useToast();
  const [saved, setSaved] = useState<Settings | null>(null);
  const [form, setForm] = useState<ModelsForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setSaved(loaded);
        setForm(modelsFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null) return;
    setSaving(true);
    setSaveError(null);
    saveSettings({
      ideasModel: form.ideasModel === '' ? null : form.ideasModel,
      planningModel: form.planningModel === '' ? null : form.planningModel,
      productionModel: form.productionModel === '' ? null : form.productionModel,
      reviewModel: form.reviewModel === '' ? null : form.reviewModel,
      ideasEffort: form.ideasEffort === '' ? null : form.ideasEffort,
      planningEffort: form.planningEffort === '' ? null : form.planningEffort,
      composeEffort: form.composeEffort === '' ? null : form.composeEffort,
      productionEffort: form.productionEffort === '' ? null : form.productionEffort,
      reviewEffort: form.reviewEffort === '' ? null : form.reviewEffort,
      critiqueEffort: form.critiqueEffort === '' ? null : form.critiqueEffort,
    })
      .then((next) => {
        setSaved(next);
        setForm(modelsFormOf(next));
        toast.ok('Models saved. They apply to the next step.');
      })
      .catch((cause: unknown) => {
        // The form keeps what was rejected, so the operator sees what to change.
        setSaveError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Panel
      id="models"
      title="Models"
      icon="gear"
      collapsible
      defaultOpen={false}
      summary={saved === null ? pendingSummary(loadError) : describeModels(saved)}
    >
      <p className="muted">
        The model each stage runs on, and how hard it thinks (Claude Code's <code>--effort</code>).
        A saved choice applies to the next step, with nothing restarted.
      </p>
      {loadError !== null && <Notice kind="error">{loadError}</Notice>}

      {form === null ? (
        loadError === null && <Skeleton lines={3} />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="field__row">
            {MODEL_STAGES.map((stage) => (
              <div className="field" key={stage.key}>
                <label className="field__label" htmlFor={`model-${stage.key}`}>
                  {stage.label}
                </label>
                <select
                  id={`model-${stage.key}`}
                  className="field__input field__input--narrow"
                  value={form[stage.key]}
                  onChange={(event) => {
                    const value = event.target.value as AgentModel | '';
                    setForm((current) =>
                      current === null ? current : { ...current, [stage.key]: value },
                    );
                  }}
                  aria-describedby={`model-${stage.key}-hint`}
                >
                  <option value="">Let Claude Code choose</option>
                  {AGENT_MODELS.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
                <p className="field__hint" id={`model-${stage.key}-hint`}>
                  {stage.hint}
                </p>
              </div>
            ))}
          </div>
          <div className="field__row">
            {EFFORT_STAGES.map((stage) => (
              <div className="field" key={stage.key}>
                <label className="field__label" htmlFor={`effort-${stage.key}`}>
                  {stage.label} effort
                </label>
                <select
                  id={`effort-${stage.key}`}
                  className="field__input field__input--narrow"
                  value={form[stage.key]}
                  onChange={(event) => {
                    const value = event.target.value as EffortLevel | '';
                    setForm((current) =>
                      current === null ? current : { ...current, [stage.key]: value },
                    );
                  }}
                  aria-describedby={`effort-${stage.key}-hint`}
                >
                  <option value="">Let Claude Code choose</option>
                  {EFFORT_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
                <p className="field__hint" id={`effort-${stage.key}-hint`}>
                  {stage.hint}
                </p>
              </div>
            ))}
          </div>
          <div className="form__actions">
            <button className="button button--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save models'}
            </button>
          </div>
        </form>
      )}
      {saveError !== null && <Notice kind="error">{saveError}</Notice>}
    </Panel>
  );
}

function describeModels(settings: Settings): string {
  const chosen = [
    ['ideas', settings.ideasModel],
    ['planning', settings.planningModel],
    ['production', settings.productionModel],
    ['review', settings.reviewModel],
  ].filter((entry): entry is [string, string] => entry[1] !== null);
  const models =
    chosen.length === 0
      ? 'Claude Code chooses'
      : chosen.map(([stage, model]) => `${stage} ${model}`).join(', ');
  return settings.composeEffort === null ? models : `${models}; compose effort ${settings.composeEffort}`;
}


/**
 * **Claude Code** (PLAN §14, FR-54): the one-time `claude auth login`.
 *
 * The login is interactive — it prints a URL and waits for a code to be pasted
 * back — so it runs in a browser terminal inside a throwaway runner container
 * whose only mount is the shared credentials volume. Every content container
 * mounts that same volume, so one sign-in covers them all.
 */
function ClaudePanel() {
  const toast = useToast();
  const { claude, setClaude } = useAppData();
  const [busy, setBusy] = useState<'start' | 'stop' | 'check' | null>(null);
  // Kept apart from `claude.login.active` so the pane stays on screen (and
  // readable) after the login process itself has exited.
  const [loginTerminal, setLoginTerminal] = useState<string | null>(null);
  const loginPane = useRef<TerminalPaneHandle>(null);
  const [loginCode, setLoginCode] = useState('');
  // Below `lg` the login terminal is not rendered at all: mounting it would
  // open a WebSocket onto a PTY too narrow to read and impossible to paste
  // a code into. The login itself keeps running on the server.
  const desktop = useMediaQuery(DESKTOP_QUERY);

  useEffect(() => {
    const controller = new AbortController();
    // A login terminal survives a page reload, so an in-progress one is picked
    // back up rather than started again.
    fetchClaudeState({ signal: controller.signal })
      .then((state) => {
        setClaude(state);
        if (state.login.terminalId !== null) setLoginTerminal(state.login.terminalId);
      })
      .catch(() => {
        // The status line below says "checking" until it can say more.
      });
    return () => controller.abort();
  }, [setClaude]);

  const run = (
    kind: NonNullable<typeof busy>,
    action: () => Promise<{ ok: boolean; text: string }>,
  ): void => {
    setBusy(kind);
    action()
      .then((result) => toast.push(result.ok ? 'ok' : 'error', result.text))
      .catch((cause: unknown) => toast.error(describeError(cause)))
      .finally(() => setBusy(null));
  };

  const onSignIn = (): void => {
    run('start', async () => {
      const state = await startClaudeLogin();
      setClaude(state);
      setLoginTerminal(state.login.terminalId);
      return {
        ok: true,
        text: 'Login terminal ready. Open the URL it prints, then paste the code back.',
      };
    });
  };

  const onCloseLogin = (): void => {
    run('stop', async () => {
      const state = await stopClaudeLogin();
      setClaude(state);
      setLoginTerminal(null);
      setLoginCode('');
      return state.status.authenticated
        ? { ok: true, text: 'Claude Code is signed in.' }
        : { ok: false, text: 'Claude Code is still not signed in.' };
    });
  };

  const onLoginExit = (): void => {
    fetchClaudeState({ refresh: true })
      .then((state) => {
        setClaude(state);
        toast.push(
          state.status.authenticated ? 'ok' : 'error',
          state.status.authenticated
            ? 'Claude Code is signed in. Close the terminal to clean up.'
            : 'The login ended without signing in. Close the terminal and try again.',
        );
      })
      .catch((cause: unknown) => toast.error(describeError(cause)));
  };

  // `claude auth login` reads the code with echo off, so nothing shows in the
  // terminal as it is typed or pasted. The field shows what is about to go in,
  // and pastes without the clipboard API, which plain HTTP does not offer.
  const onSendCode = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const code = loginCode.trim();
    if (code === '') return;
    if (loginPane.current?.send(code) !== true) {
      toast.error('Not connected to the login terminal.');
      return;
    }
    setLoginCode('');
    toast.push('ok', 'Code sent. The terminal says whether it was accepted.');
  };

  const onCheck = (): void => {
    run('check', async () => {
      const state = await fetchClaudeState({ refresh: true });
      setClaude(state);
      return state.status.authenticated
        ? { ok: true, text: 'Claude Code is signed in.' }
        : { ok: false, text: state.status.error ?? 'Claude Code is not signed in.' };
    });
  };

  const status = claude?.status ?? null;
  const signedIn = status?.authenticated === true;

  return (
    <Panel
      title="Claude Code"
      icon="zap"
      id="claude"
      collapsible
      defaultOpen={false}
      meta={status !== null && !status.authenticated && <Badge tone="danger">action needed</Badge>}
      summary={
        status === null
          ? 'checking…'
          : status.authenticated
            ? `signed in${status.account === null ? '' : ` as ${status.account}`}`
            : 'not signed in'
      }
      actions={
        <>
          <button
            type="button"
            className={signedIn ? 'button button--small' : 'button button--small button--primary'}
            onClick={onSignIn}
            disabled={busy !== null || loginTerminal !== null}
          >
            <Icon name="key" />
            {busy === 'start' ? 'Starting…' : signedIn ? 'Sign in again' : 'Sign in'}
          </button>
          <button
            type="button"
            className="button button--small button--quiet"
            onClick={onCheck}
            disabled={busy !== null}
          >
            <Icon name="sync" />
            {busy === 'check' ? 'Checking…' : 'Re-check'}
          </button>
        </>
      }
    >
      <p className={signedIn ? undefined : 'muted'}>
        {status === null
          ? 'Probing the shared credentials volume…'
          : status.authenticated
            ? `Signed in${status.account === null ? '' : ` as ${status.account}`}${status.subscription === null ? '' : ` (${status.subscription})`}. Every content container shares these credentials.`
            : 'Nothing can be planned or produced until Claude Code is signed in. It is a one-time browser login; the credentials are kept on a volume that survives restarts.'}
      </p>
      {status?.error != null && <p className="field__hint">Status check: {status.error}</p>}

      {loginTerminal !== null && (
        <div className="stack stack--tight">
          <div className="row__line">
            <span className="mono muted">{claude?.login.containerName ?? 'claude login'}</span>
            <span className="toolbar__spacer" />
            <button
              type="button"
              className="button button--small button--danger"
              onClick={onCloseLogin}
              disabled={busy !== null}
            >
              <Icon name="x" />
              {busy === 'stop' ? 'Closing…' : 'Close login terminal'}
            </button>
          </div>
          {desktop ? (
            <>
              <Suspense fallback={<Skeleton lines={6} />}>
                <TerminalPane ref={loginPane} terminalId={loginTerminal} onExit={onLoginExit} />
              </Suspense>
              <ol className="steps steps--plain steps--compact">
                <li className="step">
                  <span className="step__marker">1</span>
                  <span className="step__body">
                    Select the URL the terminal prints, copy it with Ctrl+Shift+C, open it in a new
                    tab.
                  </span>
                </li>
                <li className="step">
                  <span className="step__marker">2</span>
                  <span className="step__body">
                    Approve the request and copy the code Claude gives back.
                  </span>
                </li>
                <li className="step">
                  <span className="step__marker">3</span>
                  <span className="step__body">
                    Paste it into the field below and send it, then close the terminal once it
                    says the login worked.
                  </span>
                </li>
              </ol>
              <form className="field" onSubmit={onSendCode}>
                <label className="field__label" htmlFor="claude-login-code">
                  Code
                </label>
                <input
                  id="claude-login-code"
                  className="field__input mono"
                  autoComplete="off"
                  spellCheck={false}
                  value={loginCode}
                  onChange={(event) => setLoginCode(event.target.value)}
                  placeholder="Paste the code Claude gives back"
                />
                <div className="field__actions">
                  <button
                    type="submit"
                    className="button button--primary"
                    disabled={loginCode.trim() === ''}
                  >
                    <Icon name="check" />
                    Send code
                  </button>
                </div>
                <p className="field__hint">
                  Claude Code hides the code at its <span className="mono">Paste code here</span>{' '}
                  prompt, so the terminal shows nothing as you type or paste it. This sends it as
                  written, followed by Enter.
                </p>
              </form>
            </>
          ) : (
            <Notice kind="info">
              <strong>Finish this sign-in on a desktop.</strong> The login is an interactive
              terminal: it prints a URL to open and waits for the code you get back, which needs a
              keyboard and a wider screen. The terminal is already running on the server, so
              opening this page on a desktop picks it up where it is — or close it here and start
              again there.
            </Notice>
          )}
        </div>
      )}
    </Panel>
  );
}

/** The four on/off defaults, in the order the panel asks them (US-089). */
const DEFAULT_TOGGLES = [
  {
    field: 'defaultCaptions',
    label: 'Captions',
    hint: 'Burned-in captions. A feed plays muted, so this is normally on.',
  },
  {
    field: 'defaultMusic',
    label: 'Music',
    hint: 'A music bed under the cut, ducked beneath any voice.',
  },
  {
    field: 'defaultVoiceover',
    label: 'Narrator (voice-over)',
    hint: 'A voice over the pictures, on whichever provider the Voice panel names. Characters who speak in the script are always voiced.',
  },
  {
    field: 'defaultApproveScript',
    label: 'Approve the narration',
    hint: 'The words are already approved in the planning conversation. On, a narrated content also stops at its script mid-production, for a second read before it is spoken.',
  },
  {
    field: 'defaultWatermark',
    label: 'Watermark / logo bug',
    hint: 'The brand mark in a corner of every frame.',
  },
  {
    field: 'defaultAutopilot',
    label: 'Plan and produce without waiting for me',
    hint: 'Autopilot. A new content plans and produces by itself and skips the narration stop, whatever that switch says; your decisions are which ideas to make and whether the finished video is right. Take over on a content page to get the stops back for that one.',
  },
] as const;

type DefaultToggle = (typeof DEFAULT_TOGGLES)[number]['field'];

/** The server's window on a video's target length, in whole seconds. */
const MIN_VIDEO_SECONDS = 5;
const MAX_VIDEO_SECONDS = 90;

/**
 * **Defaults** (PLAN §14.7, US-089).
 *
 * What the new-content form starts filled in with, so the operator is not
 * re-choosing the same format, tone and four switches on every brief.
 *
 * Two things this panel is careful to say, because both are easy to assume
 * wrongly. It changes **nothing that already exists**: every one of these is
 * copied onto a content as it is created and read from there ever after, so a
 * content made yesterday keeps the answers it was made with — there is no
 * sweep and no retroactive edit. And every field stays **editable per
 * content**: this is where the form opens, not what it is held to.
 *
 * The formats and the tone are offered from the same two tables the form
 * offers, so a default can only ever be something the server would accept; a
 * row deleted later in **Format presets and tones** simply stops applying.
 */
function DefaultsPanel() {
  const toast = useToast();
  // The saved row, apart from the form, so the summary never shows an unsaved edit.
  const [saved, setSaved] = useState<Settings | null>(null);
  const [form, setForm] = useState<DefaultsForm | null>(null);
  const [presets, setPresets] = useState<FormatPreset[] | null>(null);
  const [tones, setTones] = useState<readonly ToneOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setSaved(loaded);
        setForm(defaultsFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    // The vocabulary, from the same two endpoints the new-content form reads,
    // so this panel can only offer what that form will.
    fetchPresets(controller.signal)
      .then(setPresets)
      .catch(() => undefined);
    fetchContentList(controller.signal)
      .then((list) => {
        setTones(list.tones);
      })
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null) return;
    const seconds = Number(form.defaultVideoSeconds);
    if (!Number.isInteger(seconds) || seconds < MIN_VIDEO_SECONDS || seconds > MAX_VIDEO_SECONDS) {
      setError(
        `The default video length must be a whole number of seconds between ${String(MIN_VIDEO_SECONDS)} and ${String(MAX_VIDEO_SECONDS)}.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    saveSettings({
      defaultFormats: [...form.defaultFormats],
      defaultTone: form.defaultTone,
      defaultVideoSeconds: seconds,
      defaultLanguage: form.defaultLanguage,
      defaultCaptions: form.defaultCaptions,
      defaultMusic: form.defaultMusic,
      defaultVoiceover: form.defaultVoiceover,
      defaultWatermark: form.defaultWatermark,
    })
      .then((next) => {
        setSaved(next);
        setForm(defaultsFormOf(next));
        toast.ok('Defaults saved. The next new content opens with them; nothing already made changes.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const toggleFormat = (preset: string): void => {
    setForm((current) =>
      current === null
        ? current
        : {
            ...current,
            defaultFormats: current.defaultFormats.includes(preset)
              ? current.defaultFormats.filter((entry) => entry !== preset)
              : [...current.defaultFormats, preset],
          },
    );
  };

  return (
    <Panel
      id="defaults"
      title="Defaults"
      icon="gear"
      collapsible
      defaultOpen={false}
      summary={
        saved === null
          ? pendingSummary(error)
          : `${toneLabel(tones, saved.defaultTone)} tone, ${
              saved.defaultFormats.length === 0 ? 'no formats' : saved.defaultFormats.join(', ')
            }`
      }
    >
      <p className="muted">
        What a new content&rsquo;s form is pre-filled with. Every one of them is editable per
        content.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {form === null ? (
        <Skeleton lines={4} />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="field">
            <span className="field__label">Format presets</span>
            {presets === null ? (
              <Skeleton lines={2} />
            ) : (
              <ul className="choices">
                {presets.map((preset) => (
                  <li className="choice" key={preset.preset}>
                    <label className="choice__label">
                      <input
                        type="checkbox"
                        checked={form.defaultFormats.includes(preset.preset)}
                        onChange={() => toggleFormat(preset.preset)}
                      />
                      <span className="choice__main">
                        <span className="choice__title">{preset.preset}</span>
                        <span className="choice__summary mono">{describeFormatPreset(preset)}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className="field__hint">
              Ticked here, they arrive ticked on the form — one render each. Leave them all clear
              to choose per content, which is what the form does today.
            </p>
          </div>

          <div className="field__row">
            <div className="field">
              <label className="field__label" htmlFor="default-tone">
                Default tone
              </label>
              <select
                id="default-tone"
                className="field__input field__input--narrow"
                value={form.defaultTone}
                onChange={(event) => {
                  setForm({ ...form, defaultTone: event.target.value });
                }}
                aria-describedby="default-tone-hint"
              >
                {/* The stored tone may have been deleted under Format presets
                    and tones; it stays selectable here so saving the rest of
                    the panel does not silently change it. */}
                {(tones ?? []).every((option) => option.tone !== form.defaultTone) && (
                  <option value={form.defaultTone}>{form.defaultTone}</option>
                )}
                {(tones ?? []).map((option) => (
                  <option key={option.tone} value={option.tone}>
                    {option.label ?? option.tone} — {option.summary}
                  </option>
                ))}
              </select>
              <p className="field__hint" id="default-tone-hint">
                The tone selects the pacing, cut spacing and text density every plan is measured
                against. A tone that is later deleted stops applying; the form falls back to its
                own.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="default-seconds">
                Default video length
              </label>
              <input
                id="default-seconds"
                className="field__input field__input--narrow"
                type="number"
                min={MIN_VIDEO_SECONDS}
                max={MAX_VIDEO_SECONDS}
                step={1}
                value={form.defaultVideoSeconds}
                onChange={(event) => {
                  setForm({ ...form, defaultVideoSeconds: event.target.value });
                }}
                aria-describedby="default-seconds-hint"
              />
              <p className="field__hint" id="default-seconds-hint">
                Seconds, between {MIN_VIDEO_SECONDS} and {MAX_VIDEO_SECONDS}. It becomes the
                storyboard&rsquo;s <code>Duration</code>; an image never carries one.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="default-language">
                Default language
              </label>
              <select
                id="default-language"
                className="field__input field__input--narrow"
                value={form.defaultLanguage}
                onChange={(event) => {
                  setForm({ ...form, defaultLanguage: event.target.value });
                }}
                aria-describedby="default-language-hint"
              >
                {(DEFAULT_LANGUAGE_CHOICES.includes(form.defaultLanguage)
                  ? DEFAULT_LANGUAGE_CHOICES
                  : [form.defaultLanguage, ...DEFAULT_LANGUAGE_CHOICES]
                ).map((option) => (
                  <option key={option} value={option}>
                    {languageLabel(option)}
                  </option>
                ))}
              </select>
              <p className="field__hint" id="default-language-hint">
                What a new content is written and spoken in when its product&rsquo;s profile names
                no language. Kokoro has no Dutch: a Dutch voiceover needs ElevenLabs.
              </p>
            </div>
          </div>

          <div className="field">
            <span className="field__label">On by default</span>
            <ul className="choices">
              {DEFAULT_TOGGLES.map((toggle) => (
                <li className="choice" key={toggle.field}>
                  <label className="choice__label">
                    <input
                      type="checkbox"
                      checked={form[toggle.field]}
                      onChange={() => {
                        setForm({ ...form, [toggle.field]: !form[toggle.field] });
                      }}
                    />
                    <span className="choice__main">
                      <span className="choice__title">{toggle.label}</span>
                      <span className="choice__summary">{toggle.hint}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="field__hint">
              The planning conversation is told which of captions, music and voiceover are on, so
              it plans no step against one you have switched off — a step you would pay for and
              then cut.
            </p>
          </div>

          <Notice kind="info">
            Defaults never apply backwards. Each of these is copied onto a content as it is
            created, so changing one here reaches the next new content and leaves every content
            that already exists exactly as it was made.
          </Notice>

          <div className="form__actions">
            <button className="button button--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

/** The panel as the form holds it; the length is text while it is being typed. */
interface DefaultsForm extends Record<DefaultToggle, boolean> {
  defaultFormats: string[];
  defaultTone: string;
  defaultVideoSeconds: string;
  defaultLanguage: string;
}

/** The languages the Defaults panel offers; a stored other tag stays selectable. */
const DEFAULT_LANGUAGE_CHOICES: readonly string[] = [
  'nl-NL',
  'nl-BE',
  'en-US',
  'en-GB',
  'de-DE',
  'fr-FR',
  'es-ES',
  'it-IT',
];

function defaultsFormOf(settings: Settings): DefaultsForm {
  return {
    defaultFormats: [...settings.defaultFormats],
    defaultTone: settings.defaultTone,
    defaultVideoSeconds: String(settings.defaultVideoSeconds),
    defaultLanguage: settings.defaultLanguage,
    defaultCaptions: settings.defaultCaptions,
    defaultApproveScript: settings.defaultApproveScript,
    defaultMusic: settings.defaultMusic,
    defaultVoiceover: settings.defaultVoiceover,
    defaultWatermark: settings.defaultWatermark,
    defaultAutopilot: settings.defaultAutopilot,
  };
}

/**
 * **Spend** (PLAN §14.4, US-061).
 *
 * Three numbers the operator owns — the cap a new content starts with, the
 * ceiling on the month, and where the sidebar starts warning — and beside them
 * the only figures that say whether those numbers are right: what this month
 * has actually cost, and which provider it went to. They are one panel because
 * they are one decision; a cap edited without the month in front of it is a
 * guess.
 *
 * The month-to-date comes off the same `/api/stats` poll the sidebar reads, so
 * the panel and the sidebar can never disagree, and both are the ledger's own
 * sums: an `ok` or `cached` call counts, a `failed` or `refused` one does not.
 */
function SpendPanel() {
  const { stats } = useAppData();
  const toast = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [form, setForm] = useState<SpendForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setSettings(loaded);
        setForm(spendFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null) return;
    setSaving(true);
    setError(null);
    saveSettings({
      defaultSpendCapUsd: Number(form.defaultSpendCapUsd),
      monthlySpendCapUsd: Number(form.monthlySpendCapUsd),
      spendWarnThresholdPercent: Number(form.spendWarnThresholdPercent),
      hookDuelCapUsd: Number(form.hookDuelCapUsd),
    })
      .then((saved) => {
        setSettings(saved);
        setForm(spendFormOf(saved));
        toast.ok('Spend settings saved. They apply to the next provider call.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const field = (key: keyof SpendForm, value: string): void => {
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  };

  const budget = stats?.budget ?? null;
  const byProvider = stats?.spend.byProvider ?? [];

  return (
    <Panel
      id="spend"
      title="Spend"
      icon="tag"
      collapsible
      defaultOpen={false}
      meta={budget?.warned === true && <Badge tone="wait">past the warning</Badge>}
      summary={
        stats === null
          ? 'loading…'
          : `${money(budget?.spentUsd ?? stats.spend.monthToDateUsd)} this month${
              budget === null || budget.capUsd === null
                ? ', no monthly cap'
                : ` of the ${money(budget.capUsd)} cap`
            }`
      }
    >
      <p className="muted">
        What a content may cost, what a month may cost, and what this one has.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <div className="spend">
        <div className="spend__figure">
          <span className="spend__label">Month to date{budget === null ? '' : ` (${budget.month})`}</span>
          <span className="spend__value">{money(budget?.spentUsd ?? stats?.spend.monthToDateUsd ?? 0)}</span>
          <span className="muted">
            {budget === null || budget.capUsd === null
              ? 'No monthly cap is set, so no call is refused for the month.'
              : `of the ${money(budget.capUsd)} monthly cap${
                  budget.warnAtUsd === null
                    ? ''
                    : `, warning at ${money(budget.warnAtUsd)} (${String(budget.warnThresholdPercent)}%)`
                }.`}
          </span>
        </div>
        {budget !== null && budget.capUsd !== null && budget.capUsd > 0 && (
          <Gauge
            value={budget.spentUsd / budget.capUsd}
            label={`${money(budget.spentUsd)} of the ${money(budget.capUsd)} monthly cap`}
          />
        )}
        {budget?.warned === true && (
          <Notice kind="warn">
            This month has passed your {String(budget.warnThresholdPercent)}% warning threshold.
            The sidebar says so too, until the month turns or the cap is raised.
          </Notice>
        )}
      </div>

      {byProvider.length === 0 ? (
        <p className="muted">
          <Icon name="list" /> Nothing has been charged this month. Every provider call is
          recorded here with what it cost — including the ones served from the cache, at nothing,
          and the ones a cap refused.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Provider</th>
              <th scope="col">Calls</th>
              <th scope="col">This month</th>
            </tr>
          </thead>
          <tbody>
            {byProvider.map((provider) => (
              <tr key={provider.provider}>
                <td>{provider.provider}</td>
                <td className="num">{String(provider.calls)}</td>
                <td className="num">{money(provider.costUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form === null ? (
        <Skeleton lines={3} />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="field__row">
            <div className="field">
              <label className="field__label" htmlFor="spend-default-cap">
                Default cap per content (USD)
              </label>
              <input
                id="spend-default-cap"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={10000}
                step={0.01}
                value={form.defaultSpendCapUsd}
                onChange={(event) => {
                  field('defaultSpendCapUsd', event.target.value);
                }}
                aria-describedby="spend-default-cap-hint"
              />
              <p className="field__hint" id="spend-default-cap-hint">
                What the new-content form is pre-filled with. A content keeps the cap it was
                created with, so changing this never moves an existing one.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="spend-monthly-cap">
                Monthly cap (USD)
              </label>
              <input
                id="spend-monthly-cap"
                className="field__input field__input--narrow"
                type="number"
                min={0}
                max={10000}
                step={0.01}
                value={form.monthlySpendCapUsd}
                onChange={(event) => {
                  field('monthlySpendCapUsd', event.target.value);
                }}
                aria-describedby="spend-monthly-cap-hint"
              />
              <p className="field__hint" id="spend-monthly-cap-hint">
                Every content together, in one UTC calendar month. 0 is no monthly ceiling at all.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="spend-warn">
                Warn me at (% of the monthly cap)
              </label>
              <input
                id="spend-warn"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={100}
                step={1}
                value={form.spendWarnThresholdPercent}
                onChange={(event) => {
                  field('spendWarnThresholdPercent', event.target.value);
                }}
                aria-describedby="spend-warn-hint"
              />
              <p className="field__hint" id="spend-warn-hint">
                Where the sidebar's spend meter starts warning. It warns; it refuses nothing.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="spend-hook-duel">
                Hook duel cap (USD per run)
              </label>
              <input
                id="spend-hook-duel"
                className="field__input field__input--narrow"
                type="number"
                min={0}
                max={5}
                step={0.01}
                value={form.hookDuelCapUsd}
                onChange={(event) => {
                  field('hookDuelCapUsd', event.target.value);
                }}
                aria-describedby="spend-hook-duel-hint"
              />
              <p className="field__hint" id="spend-hook-duel-hint">
                What one <code>mc critique hooks</code> may spend on the critic model choosing a plan's hooks. Over it
                the duel is skipped with a note. 0 never duels.
              </p>
            </div>
          </div>

          <div className="form__actions">
            <button className="button button--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save spend settings'}
            </button>
            {settings !== null && (
              <span className="muted">
                A saved cap applies to the next provider call — nothing has to be restarted.
              </span>
            )}
          </div>
        </form>
      )}
    </Panel>
  );
}

/** The spend fields as typed, so a half-typed number is not a saved one. */
interface SpendForm {
  defaultSpendCapUsd: string;
  monthlySpendCapUsd: string;
  spendWarnThresholdPercent: string;
  hookDuelCapUsd: string;
}

function spendFormOf(settings: Settings): SpendForm {
  return {
    defaultSpendCapUsd: String(settings.defaultSpendCapUsd),
    monthlySpendCapUsd: String(settings.monthlySpendCapUsd),
    spendWarnThresholdPercent: String(settings.spendWarnThresholdPercent),
    hookDuelCapUsd: String(settings.hookDuelCapUsd),
  };
}

/** Money, to the cent, wherever this page prints a figure. */
function money(usd: number): string {
  return `${usd.toFixed(2)}`;
}

/**
 * **Production** (PLAN §14.5, US-009 and US-077).
 *
 * Six numbers that decide how much of the host one production may take and how
 * long it may take it for. The two that matter most are at the right of the
 * row: a content container's `Memory` and `NanoCpus` ceilings, which is what
 * keeps a render — headless Chromium plus ffmpeg — from starving the machine
 * the server itself runs on.
 *
 * They are read when a container's `HostConfig` is built, so a saved value
 * reaches the *next* container start and never a container that is already up.
 * That is stated on the panel rather than implied, because the operator's
 * question after saving is exactly "is that in force now".
 *
 * The host's own cores and memory sit above the form, from the same `/api/stats`
 * poll the sidebar reads, because a ceiling is only right relative to the
 * machine: the sum of the slots is what the host has to survive, and the panel
 * says so when that sum passes what is there.
 */
function ProductionPanel() {
  const { stats } = useAppData();
  const toast = useToast();
  const [saved, setSaved] = useState<Settings | null>(null);
  const [form, setForm] = useState<ProductionForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setSaved(loaded);
        setForm(productionFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null) return;
    setSaving(true);
    setError(null);
    saveSettings({
      maxConcurrentProductions: Number(form.maxConcurrentProductions),
      stepTimeoutMinutes: Number(form.stepTimeoutMinutes),
      renderTimeoutMinutes: Number(form.renderTimeoutMinutes),
      containerMemoryGb: Number(form.containerMemoryGb),
      containerCpus: Number(form.containerCpus),
      maxParallelSteps: Number(form.maxParallelSteps),
      brandRefreshDays: Number(form.brandRefreshDays),
    })
      .then((next) => {
        setSaved(next);
        setForm(productionFormOf(next));
        toast.ok(
          'Production settings saved. The memory and CPU ceilings apply to the next container start.',
        );
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const field = (key: keyof ProductionForm, value: string): void => {
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  };

  // What the form in front of the operator would cost if every slot were full,
  // against what the machine has. Both readings come from the form rather than
  // from the saved settings, so the warning appears as the number is typed.
  const host = stats?.host ?? null;
  const slots = Number(form?.maxConcurrentProductions ?? 0);
  const perContainerGb = Number(form?.containerMemoryGb ?? 0);
  const perContainerCpus = Number(form?.containerCpus ?? 0);
  const demand =
    Number.isFinite(slots) && Number.isFinite(perContainerGb) && Number.isFinite(perContainerCpus)
      ? { memoryGb: slots * perContainerGb, cpus: slots * perContainerCpus }
      : null;
  const hostGb = host === null ? null : host.memory.total / 1024 ** 3;
  const overcommitted =
    demand !== null &&
    host !== null &&
    hostGb !== null &&
    (demand.memoryGb > hostGb || demand.cpus > host.cores);

  return (
    <Panel
      id="production"
      title="Production"
      icon="gear"
      collapsible
      defaultOpen={false}
      summary={
        saved === null
          ? pendingSummary(error)
          : `${String(saved.maxConcurrentProductions)} slot${
              saved.maxConcurrentProductions === 1 ? '' : 's'
            }, step timeout ${String(saved.stepTimeoutMinutes)} min`
      }
    >
      <p className="muted">
        How many productions run at once, how long a step may take, and what one container may
        use.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <Facts
        items={[
          {
            label: 'This machine',
            value:
              host === null ? (
                <span className="muted">reading…</span>
              ) : (
                `${String(host.cores)} core${host.cores === 1 ? '' : 's'}, ${gigabytes(
                  host.memory.total,
                )} GB of RAM`
              ),
          },
          {
            label: 'In force now',
            value:
              stats === null ? (
                <span className="muted">reading…</span>
              ) : (
                `${String(stats.containers.memoryGb)} GB and ${String(
                  stats.containers.cpus,
                )} CPU${stats.containers.cpus === 1 ? '' : 's'} per container`
              ),
          },
          {
            label: 'Every slot full would want',
            value:
              demand === null ? (
                <span className="muted">—</span>
              ) : (
                `${String(round1(demand.memoryGb))} GB and ${String(round1(demand.cpus))} CPUs`
              ),
          },
        ]}
      />

      {overcommitted && (
        <Notice kind="warn">
          Every slot full would ask this machine for more than it has. Docker will not refuse the
          start — the kernel will kill whichever container reaches the limit first, mid-render. Two
          concurrent productions want roughly 4 CPUs and 12 GB of RAM —{' '}
          <code className="mono">docs/architecture.md</code> works the sizing out in full.
        </Notice>
      )}

      {form === null ? (
        <Skeleton lines={3} />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="field__row">
            <div className="field">
              <label className="field__label" htmlFor="production-slots">
                Max concurrent productions
              </label>
              <input
                id="production-slots"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={50}
                step={1}
                value={form.maxConcurrentProductions}
                onChange={(event) => {
                  field('maxConcurrentProductions', event.target.value);
                }}
                aria-describedby="production-slots-hint"
              />
              <p className="field__hint" id="production-slots-hint">
                The slot meter in the sidebar counts against this. Lowering it under running work
                starves nothing: the running contents finish, and the queue waits.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="production-step-timeout">
                Step timeout (minutes)
              </label>
              <input
                id="production-step-timeout"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={720}
                step={1}
                value={form.stepTimeoutMinutes}
                onChange={(event) => {
                  field('stepTimeoutMinutes', event.target.value);
                }}
                aria-describedby="production-step-timeout-hint"
              />
              <p className="field__hint" id="production-step-timeout-hint">
                One ordinary step. A step that passes it is stopped and counts an attempt.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="production-render-timeout">
                Render step timeout (minutes)
              </label>
              <input
                id="production-render-timeout"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={720}
                step={1}
                value={form.renderTimeoutMinutes}
                onChange={(event) => {
                  field('renderTimeoutMinutes', event.target.value);
                }}
                aria-describedby="production-render-timeout-hint"
              />
              <p className="field__hint" id="production-render-timeout-hint">
                Renders and variants get their own, longer cap: 600 frames of Chromium capture plus
                an x264 encode is minutes of work even on a fast host.
              </p>
            </div>
          </div>

          <div className="field__row">
            <div className="field">
              <label className="field__label" htmlFor="production-memory">
                Container memory (GB)
              </label>
              <input
                id="production-memory"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={128}
                step={1}
                value={form.containerMemoryGb}
                onChange={(event) => {
                  field('containerMemoryGb', event.target.value);
                }}
                aria-describedby="production-memory-hint"
              />
              <p className="field__hint" id="production-memory-hint">
                <code className="mono">HostConfig.Memory</code> per content container. Chromium and
                ffmpeg need headroom: at 1 GB a render is killed part-way through.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="production-cpus">
                Container CPUs
              </label>
              <input
                id="production-cpus"
                className="field__input field__input--narrow"
                type="number"
                min={0.5}
                max={64}
                step={0.25}
                value={form.containerCpus}
                onChange={(event) => {
                  field('containerCpus', event.target.value);
                }}
                aria-describedby="production-cpus-hint"
              />
              <p className="field__hint" id="production-cpus-hint">
                <code className="mono">HostConfig.NanoCpus</code>, exactly as{' '}
                <code className="mono">docker run --cpus</code> takes it — fractions are allowed,
                so 1.5 is a container and a half's worth of encoding.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="production-parallel">
                Asset steps side by side
              </label>
              <input
                id="production-parallel"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={6}
                step={1}
                value={form.maxParallelSteps}
                onChange={(event) => {
                  field('maxParallelSteps', event.target.value);
                }}
                aria-describedby="production-parallel-hint"
              />
              <p className="field__hint" id="production-parallel-hint">
                How many of a content's captures, pictures, music, effects and talking heads are made
                at once, in its one container. 1 makes every step on its own. The words, the voice,
                the composition and the renders always run alone.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="production-brand-refresh">
                Refresh brand profiles older than (days)
              </label>
              <input
                id="production-brand-refresh"
                className="field__input field__input--narrow"
                type="number"
                min={1}
                max={365}
                step={1}
                value={form.brandRefreshDays}
                onChange={(event) => {
                  field('brandRefreshDays', event.target.value);
                }}
                aria-describedby="production-brand-refresh-hint"
              />
              <p className="field__hint" id="production-brand-refresh-hint">
                Creating a content re-extracts a repository's brand profile once it is this old. A
                profile that failed is never retried automatically — a broken clone fails the same
                way every time.
              </p>
            </div>
          </div>

          <div className="form__actions">
            <button className="button button--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save production settings'}
            </button>
            <span className="muted">
              The timeouts and the slot cap apply to the next step; the memory and CPU ceilings to
              the next container start. A container that is already up keeps the ceiling it was
              started with.
            </span>
          </div>
        </form>
      )}
    </Panel>
  );
}

/** One decimal, so a fractional CPU sum is not printed to fifteen places. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** The seven fields as typed, so a half-typed number is not a saved one. */
interface ProductionForm {
  maxConcurrentProductions: string;
  stepTimeoutMinutes: string;
  renderTimeoutMinutes: string;
  containerMemoryGb: string;
  containerCpus: string;
  maxParallelSteps: string;
  brandRefreshDays: string;
}

function productionFormOf(settings: Settings): ProductionForm {
  return {
    maxConcurrentProductions: String(settings.maxConcurrentProductions),
    stepTimeoutMinutes: String(settings.stepTimeoutMinutes),
    renderTimeoutMinutes: String(settings.renderTimeoutMinutes),
    containerMemoryGb: String(settings.containerMemoryGb),
    containerCpus: String(settings.containerCpus),
    maxParallelSteps: String(settings.maxParallelSteps),
    brandRefreshDays: String(settings.brandRefreshDays),
  };
}


/**
 * The voice a step speaks in when its plan does not say (US-063).
 *
 * Two rows. `Kokoro` is Hyperframes' free voice for English and a few other
 * languages — never Dutch — but it needs Python, which the runner image does
 * not have, so today it does not run at all. ElevenLabs is the voice that
 * does: selecting it here routes `mc tts` through the gateway, and it still has
 * to be switched on and keyed under Providers above. A Dutch content goes to
 * ElevenLabs whatever this says, and gets no voiceover while it is off.
 */
const TTS_PROVIDER_NAMES: Record<TtsProvider, string> = {
  kokoro: 'Kokoro',
  elevenlabs: 'ElevenLabs',
};

function VoicePanel() {
  const toast = useToast();
  const [saved, setSaved] = useState<VoiceForm | null>(null);
  const [form, setForm] = useState<VoiceForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setSaved({ ttsProvider: loaded.ttsProvider, ttsVoice: loaded.ttsVoice });
        setForm({ ttsProvider: loaded.ttsProvider, ttsVoice: loaded.ttsVoice });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null) return;
    setSaving(true);
    setError(null);
    saveSettings({ ttsProvider: form.ttsProvider, ttsVoice: form.ttsVoice.trim() })
      .then((next) => {
        setSaved({ ttsProvider: next.ttsProvider, ttsVoice: next.ttsVoice });
        setForm({ ttsProvider: next.ttsProvider, ttsVoice: next.ttsVoice });
        toast.ok('Voice settings saved. They apply to the next voiceover step.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Panel
      id="voice"
      title="Voice"
      icon="gear"
      collapsible
      defaultOpen={false}
      summary={
        saved === null
          ? pendingSummary(error)
          : `${TTS_PROVIDER_NAMES[saved.ttsProvider]}, ${
              saved.ttsVoice === '' ? 'its default voice' : saved.ttsVoice
            }`
      }
    >
      <p className="muted">
        Who speaks a voiceover, and in which voice, when the plan does not say.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {form === null ? (
        <Skeleton lines={2} />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="field__row">
            <div className="field">
              <label className="field__label" htmlFor="tts-provider">
                Default provider
              </label>
              <select
                id="tts-provider"
                className="field__input field__input--narrow"
                value={form.ttsProvider}
                onChange={(event) => {
                  setForm({ ...form, ttsProvider: event.target.value as TtsProvider });
                }}
                aria-describedby="tts-provider-hint"
              >
                <option value="kokoro">Kokoro — free, English and a few other languages</option>
                <option value="elevenlabs">ElevenLabs — through the gateway, paid</option>
              </select>
              <p className="field__hint" id="tts-provider-hint">
                Kokoro is Hyperframes&rsquo; free voice for English and a few other languages,
                never Dutch: a Dutch content is spoken by ElevenLabs whatever this says, and gets
                no voiceover while ElevenLabs is off. The current runner image has no Python, so
                Kokoro does not run yet. ElevenLabs has to be switched on and keyed under
                Providers.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="tts-voice">
                Default voice
              </label>
              <input
                id="tts-voice"
                className="field__input field__input--narrow"
                value={form.ttsVoice}
                onChange={(event) => {
                  setForm({ ...form, ttsVoice: event.target.value });
                }}
                aria-describedby="tts-voice-hint"
              />
              <p className="field__hint" id="tts-voice-hint">
                A voice id on the provider above — <code>af_heart</code> is Kokoro&rsquo;s
                American-English default and is never used for another language. A plan may name
                another voice for one step, and ElevenLabs keeps its own default on its Providers
                card.
              </p>
            </div>
          </div>
          <div className="form__actions">
            <button className="button button--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save voice settings'}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

/** The two rows as the form holds them, before they are saved. */
interface VoiceForm {
  ttsProvider: TtsProvider;
  ttsVoice: string;
}

/** The empty form, before the server's own values land. */
interface StorageForm {
  endpoint: string;
  region: string;
  bucket: string;
  accessKeyId: string;
  /** Empty means "leave the stored secret alone"; it is never filled in. */
  secretAccessKey: string;
  keyPrefix: string;
  addressingStyle: AddressingStyle;
  publicBaseUrl: string;
  keepCopiesOnDelete: boolean;
}

function formOf(view: StorageView): StorageForm {
  const storage = view.storage;
  return {
    endpoint: storage.endpoint ?? '',
    region: storage.region,
    bucket: storage.bucket,
    accessKeyId: storage.accessKeyId,
    secretAccessKey: '',
    keyPrefix: storage.keyPrefix,
    addressingStyle: storage.addressingStyle,
    publicBaseUrl: storage.publicBaseUrl ?? '',
    keepCopiesOnDelete: storage.keepCopiesOnDelete,
  };
}

function StoragePanel() {
  const { setStorage } = useAppData();
  const toast = useToast();
  const [view, setView] = useState<StorageView | null>(null);
  const [form, setForm] = useState<StorageForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<StorageTestResult | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchStorage(controller.signal)
      .then((loaded) => {
        setView(loaded);
        setForm(formOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const field = useCallback(<K extends keyof StorageForm>(key: K, value: StorageForm[K]): void => {
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null || saving) return;
    setSaving(true);
    setError(null);
    // The write is partial, so an untouched secret is simply not sent: there
    // is no value the page could send that means "the one you already have".
    const update: StorageSettingsUpdate = {
      endpoint: form.endpoint.trim() === '' ? null : form.endpoint.trim(),
      region: form.region.trim(),
      bucket: form.bucket.trim(),
      accessKeyId: form.accessKeyId.trim(),
      keyPrefix: form.keyPrefix.trim(),
      addressingStyle: form.addressingStyle,
      publicBaseUrl: form.publicBaseUrl.trim() === '' ? null : form.publicBaseUrl.trim(),
      keepCopiesOnDelete: form.keepCopiesOnDelete,
      ...(form.secretAccessKey === '' ? {} : { secretAccessKey: form.secretAccessKey }),
    };
    saveStorage(update)
      .then((saved) => {
        setView(saved);
        setForm(formOf(saved));
        setStorage(saved.storage);
        toast.ok('Storage settings saved.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const runTest = (): void => {
    if (testing) return;
    setTesting(true);
    setError(null);
    setResult(null);
    testStorage()
      .then((outcome) => {
        setResult(outcome);
        setStorage(outcome.storage);
        setView((current) => (current === null ? current : { ...current, storage: outcome.storage }));
        if (outcome.ok) toast.ok('The bucket answered every check.');
        else toast.error(outcome.message);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setTesting(false);
      });
  };

  if (view === null || form === null) {
    return (
      <Panel
        id="storage"
        title="Storage"
        icon="package"
        collapsible
        defaultOpen={false}
        summary={pendingSummary(error)}
      >
        {error === null ? <Skeleton lines={6} /> : <Notice kind="error">{error}</Notice>}
      </Panel>
    );
  }

  const storage = view.storage;
  const last = storage.lastTest;

  return (
    <Panel
      id="storage"
      title="Storage"
      icon="package"
      collapsible
      defaultOpen={false}
      summary={storage.configured ? storage.bucket : 'not configured'}
      meta={
        !storage.configured ? (
          <Badge tone="danger">action needed</Badge>
        ) : last === null ? (
          <Badge tone="wait">never tested</Badge>
        ) : (
          <Badge tone={last.ok ? 'done' : 'danger'}>
            {last.ok ? 'last test passed' : 'last test failed'}
          </Badge>
        )
      }
      actions={
        <button
          type="button"
          className="button"
          onClick={runTest}
          disabled={testing || !storage.configured}
          title={
            storage.configured
              ? 'HeadBucket, a write, a signed read, a CORS preflight and a delete'
              : 'Save a bucket, an access key id and a secret first'
          }
        >
          <Icon name="pulse" />
          {testing ? 'Testing…' : 'Test connection'}
        </button>
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {last !== null && result === null && (
        <p className="muted">
          Last tested {localTime(last.at)}: {last.message}
        </p>
      )}
      {result !== null && <TestResult result={result} />}

      <form className="form" onSubmit={submit}>
        <div className="field">
          <label className="field__label" htmlFor="storage-endpoint">
            Endpoint
          </label>
          <input
            id="storage-endpoint"
            className="field__input"
            value={form.endpoint}
            onChange={(event) => {
              field('endpoint', event.target.value);
            }}
            placeholder="https://fsn1.your-objectstorage.com"
            autoComplete="off"
            spellCheck={false}
            aria-describedby="storage-endpoint-hint"
          />
          <p className="field__hint" id="storage-endpoint-hint">
            The S3 API endpoint, with no path. Leave it empty for AWS S3, where the region is
            enough.
          </p>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="storage-region">
              Region
            </label>
            <input
              id="storage-region"
              className="field__input field__input--narrow"
              value={form.region}
              onChange={(event) => {
                field('region', event.target.value);
              }}
              placeholder="fsn1"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="storage-bucket">
              Bucket
            </label>
            <input
              id="storage-bucket"
              className="field__input"
              value={form.bucket}
              onChange={(event) => {
                field('bucket', event.target.value);
              }}
              placeholder="marketing-assets"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="storage-access-key">
            Access key id
          </label>
          <input
            id="storage-access-key"
            className="field__input"
            value={form.accessKeyId}
            onChange={(event) => {
              field('accessKeyId', event.target.value);
            }}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="storage-secret">
            Secret access key
          </label>
          <input
            id="storage-secret"
            className="field__input"
            type="password"
            value={form.secretAccessKey}
            onChange={(event) => {
              field('secretAccessKey', event.target.value);
            }}
            placeholder={
              storage.secretConfigured
                ? `stored, ending ${storage.secretLastFour ?? '••••'}`
                : 'paste the secret'
            }
            autoComplete="new-password"
            spellCheck={false}
            aria-describedby="storage-secret-hint"
          />
          <p className="field__hint" id="storage-secret-hint">
            Write-only: only its last four characters ever come back out. Leave it empty to keep
            the stored one.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="storage-prefix">
            Key prefix
          </label>
          <input
            id="storage-prefix"
            className="field__input"
            value={form.keyPrefix}
            onChange={(event) => {
              field('keyPrefix', event.target.value);
            }}
            placeholder="marketingchief/"
            autoComplete="off"
            spellCheck={false}
            aria-describedby="storage-prefix-hint"
          />
          <p className="field__hint" id="storage-prefix-hint">
            A path inside the bucket every key is written under. Empty means the bucket root.
          </p>
        </div>

        <div className="field">
          <span className="field__label">Addressing style</span>
          <div className="field__actions">
            {view.addressingStyles.map((style) => (
              <label className="radio" key={style}>
                <input
                  type="radio"
                  name="storage-addressing"
                  value={style}
                  checked={form.addressingStyle === style}
                  onChange={() => {
                    field('addressingStyle', style);
                  }}
                />
                {style === 'virtual-hosted' ? 'Virtual-hosted' : 'Path'}
              </label>
            ))}
          </div>
          <p className="field__hint">
            {form.addressingStyle === 'virtual-hosted'
              ? 'The bucket goes in the host name — bucket.endpoint/key. What Hetzner, AWS and R2 want.'
              : 'The bucket stays in the path — endpoint/bucket/key. What MinIO needs.'}
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="storage-public-url">
            Public base URL
          </label>
          <input
            id="storage-public-url"
            className="field__input"
            value={form.publicBaseUrl}
            onChange={(event) => {
              field('publicBaseUrl', event.target.value);
            }}
            placeholder="https://cdn.example.com"
            autoComplete="off"
            spellCheck={false}
            aria-describedby="storage-public-url-hint"
          />
          <p className="field__hint" id="storage-public-url-hint">
            The CDN or custom domain assets are served from, if there is one. It is the host the
            CORS preflight is sent to, because it is the host a browser asks.
          </p>
        </div>

        <div className="field">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.keepCopiesOnDelete}
              onChange={(event) => {
                field('keepCopiesOnDelete', event.target.checked);
              }}
            />
            Keep copies on delete
          </label>
          <p className="field__hint">
            Deleting a content removes it from marketingchief. The published video may still be
            being served out of the bucket.
          </p>
        </div>

        <div className="field__actions">
          <button type="submit" className="button button--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save storage settings'}
          </button>
        </div>
      </form>

      <CorsPanel
        json={result?.cors ?? view.cors.json}
        help={view.cors.help}
        origin={view.cors.origin}
      />
      <StorageProvidersPanel view={view} />
    </Panel>
  );
}

const CHECK_TONE: Record<StorageCheckResult['status'], string> = {
  passed: 'stage--done',
  failed: 'stage--failed',
  skipped: '',
};

/** The five checks as the panel lists them, with the SDK's own words kept. */
function TestResult({ result }: { readonly result: StorageTestResult }) {
  return (
    <>
      <Notice kind={result.ok ? 'ok' : 'error'}>{result.message}</Notice>
      <ol className="stages" aria-label="Connection test">
        {result.checks.map((check) => (
          <li className={`stage ${CHECK_TONE[check.status]}`.trim()} key={check.name}>
            <span className="stage__marker">
              {check.status === 'passed' ? (
                <Icon name="check" />
              ) : check.status === 'failed' ? (
                <Icon name="x" />
              ) : (
                <Icon name="clock" />
              )}
            </span>
            <span className="stage__text">
              <span className="stage__label">{check.label}</span>
              <span className="stage__detail">{check.detail}</span>
              {check.error !== null && (
                <span className="output output--wrap">
                  {check.code === null ? '' : `${check.code}: `}
                  {check.error}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </>
  );
}

/**
 * The rule to paste into the bucket. Printed whether or not the preflight
 * passed, because a bucket that has never been tested is exactly the one whose
 * operator needs it.
 */
function CorsPanel({
  json,
  help,
  origin,
}: {
  readonly json: string;
  readonly help: string;
  readonly origin: string;
}) {
  const toast = useToast();
  const copy = (): void => {
    navigator.clipboard
      .writeText(json)
      .then(() => {
        toast.ok('CORS rule copied.');
      })
      .catch(() => {
        toast.error('The browser blocked the clipboard; select the text instead.');
      });
  };
  return (
    <Panel
      title="CORS rule for the bucket"
      icon="key"
      actions={
        <button type="button" className="button button--small" onClick={copy}>
          <Icon name="copy" />
          Copy
        </button>
      }
    >
      <p className="muted">{help}</p>
      <pre className="output output--wrap">{json}</pre>
      <p className="field__hint">
        Written for this install’s origin, <span className="mono">{origin}</span>. Neither PUT nor
        DELETE is in it: the browser never writes to the bucket — every upload is made by the
        server.
      </p>
    </Panel>
  );
}

/** What to put in the fields above, per provider, straight from the server. */
function StorageProvidersPanel({ view }: { readonly view: StorageView }) {
  return (
    <Panel title="What each provider wants" icon="stack">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Endpoint</th>
              <th>Region</th>
              <th>Addressing</th>
              <th>Watch out for</th>
            </tr>
          </thead>
          <tbody>
            {view.providers.map((provider) => (
              <tr key={provider.name}>
                <td>{provider.name}</td>
                <td className="mono">{provider.endpoint === '' ? '—' : provider.endpoint}</td>
                <td className="mono">{provider.region}</td>
                <td>{provider.addressingStyle}</td>
                <td>{provider.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ meta ads */

/** The Meta Ads form. The token field starts empty and stays empty after a save. */
interface MetaForm {
  /** Empty means "leave the stored token alone"; it is never filled in. */
  accessToken: string;
  adAccountId: string;
  pageId: string;
  instagramId: string;
  pixelId: string;
}

function metaFormOf(meta: MetaSettings): MetaForm {
  return {
    accessToken: '',
    adAccountId: meta.adAccountId,
    pageId: meta.pageId,
    instagramId: meta.instagramId ?? '',
    pixelId: meta.pixelId,
  };
}

/**
 * The Meta Ads connection (meta-ads-agent US-001): a System User token and the
 * ids of the ad account, Page, Instagram account and Pixel the ads agent works
 * on. The token is write-only, like the bucket secret: an empty field on save
 * keeps it, and **Remove token** — behind a confirmation — is the only way to
 * take it away.
 */
const META_STATUS_LABEL: Record<MetaConnectionStatus, string> = {
  not_tested: 'Not tested',
  connected: 'Connected',
  token_invalid: 'Token invalid',
  check_failed: 'Check failed',
};

const META_STATUS_TONE: Record<MetaConnectionStatus, 'wait' | 'done' | 'danger'> = {
  not_tested: 'wait',
  connected: 'done',
  token_invalid: 'danger',
  check_failed: 'danger',
};

const META_CHECK_TONE: Record<MetaCheckResult['status'], string> = {
  passed: 'stage--done',
  failed: 'stage--failed',
  warning: 'stage--warning',
  skipped: '',
};

/**
 * The Meta checks as a list, one to a row: unlike the bucket's five, each can
 * carry a sentence of what to do, which a four-across strip would cut off.
 */
function MetaTestResult({ result }: { readonly result: MetaConnectionTestResult }) {
  return (
    <>
      <Notice kind={result.ok ? 'ok' : 'error'}>{result.message}</Notice>
      <ol className="stages stages--list" aria-label="Connection test">
        {result.checks.map((check) => (
          <li className={`stage ${META_CHECK_TONE[check.status]}`.trim()} key={check.name}>
            <span className="stage__marker">
              {check.status === 'passed' ? (
                <Icon name="check" />
              ) : check.status === 'failed' ? (
                <Icon name="x" />
              ) : check.status === 'warning' ? (
                <Icon name="alert" />
              ) : (
                <Icon name="clock" />
              )}
            </span>
            <span className="stage__text">
              <span className="stage__label">
                {check.label}
                {check.status === 'warning' && ' — warning'}
              </span>
              <span className="stage__detail">{check.detail}</span>
              {check.fix !== null && <span className="stage__fix">{check.fix}</span>}
              {check.error !== null && (
                <span className="output output--wrap">
                  {check.code === null ? '' : `${String(check.code)}: `}
                  {check.error}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </>
  );
}

function MetaAdsPanel() {
  const toast = useToast();
  const [meta, setMeta] = useState<MetaSettings | null>(null);
  const [form, setForm] = useState<MetaForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<MetaConnectionTestResult | null>(null);
  const [idsLocked, setIdsLocked] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchMeta(controller.signal)
      .then((loaded) => {
        setMeta(loaded.meta);
        setIdsLocked(loaded.idsLocked);
        setForm(metaFormOf(loaded.meta));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const field = useCallback(<K extends keyof MetaForm>(key: K, value: MetaForm[K]): void => {
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  }, []);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form === null || saving) return;
    setSaving(true);
    setError(null);
    // An untouched token is simply not sent, so a save of the ids keeps it.
    const token = form.accessToken.trim();
    // Locked ids are not sent: the server would refuse a change, and keeps them anyway.
    const update: MetaSettingsUpdate = {
      ...(idsLocked
        ? {}
        : { adAccountId: form.adAccountId.trim(), pageId: form.pageId.trim(), pixelId: form.pixelId.trim() }),
      instagramId: form.instagramId.trim() === '' ? null : form.instagramId.trim(),
      ...(token === '' ? {} : { accessToken: token }),
    };
    saveMeta(update)
      .then((saved) => {
        setMeta(saved.meta);
        setIdsLocked(saved.idsLocked);
        setForm(metaFormOf(saved.meta));
        if (saved.meta.connectionStatus === 'not_tested') setResult(null);
        toast.ok('Meta Ads settings saved.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const removeToken = (): void => {
    if (removing) return;
    setRemoving(true);
    setError(null);
    removeMetaToken()
      .then((saved) => {
        setMeta(saved.meta);
        setIdsLocked(saved.idsLocked);
        setForm((current) => (current === null ? current : { ...current, accessToken: '' }));
        setConfirming(false);
        toast.ok('Meta access token removed.');
      })
      .catch((cause: unknown) => {
        setConfirming(false);
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setRemoving(false);
      });
  };

  const runTest = (): void => {
    if (testing) return;
    setTesting(true);
    setError(null);
    setResult(null);
    testMetaConnection()
      .then((outcome) => {
        setResult(outcome);
        setMeta(outcome.meta);
        if (outcome.ok) toast.ok(outcome.message);
        else toast.error(outcome.message);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setTesting(false);
      });
  };

  if (meta === null || form === null) {
    return (
      <Panel
        id="meta"
        title="Meta Ads"
        icon="rocket"
        collapsible
        defaultOpen={false}
        summary={pendingSummary(error)}
      >
        {error === null ? <Skeleton lines={5} /> : <Notice kind="error">{error}</Notice>}
      </Panel>
    );
  }

  return (
    <Panel
      id="meta"
      title="Meta Ads"
      icon="rocket"
      collapsible
      defaultOpen={false}
      summary={
        !meta.configured ? 'not connected' : (meta.account?.name ?? meta.adAccountId)
      }
      meta={
        <Badge tone={META_STATUS_TONE[meta.connectionStatus]}>
          {META_STATUS_LABEL[meta.connectionStatus]}
        </Badge>
      }
      actions={
        <>
          <button
            type="button"
            className="button"
            onClick={runTest}
            disabled={testing || !meta.configured}
            title={
              meta.configured
                ? 'Token, permissions, ad account, payment method, Page, Pixel, events and landing page'
                : 'Save a token, an ad account, a Page and a Pixel first'
            }
          >
            <Icon name="pulse" />
            {testing ? 'Testing…' : 'Test connection'}
          </button>
        </>
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {meta.account !== null && (
        <dl className="facts" aria-label="Ad account">
          <div className="facts__row">
            <dt>Account</dt>
            <dd>{meta.account.name}</dd>
          </div>
          <div className="facts__row">
            <dt>Currency</dt>
            <dd>{meta.account.currency}</dd>
          </div>
          <div className="facts__row">
            <dt>Timezone</dt>
            <dd>{meta.account.timezone}</dd>
          </div>
        </dl>
      )}
      {meta.lastTest !== null && result === null && (
        <p className="muted">
          Last tested {localTime(meta.lastTest.at)}: {meta.lastTest.message}
        </p>
      )}
      {result !== null && <MetaTestResult result={result} />}

      <form className="form" onSubmit={submit}>
        <div className="field">
          <label className="field__label" htmlFor="meta-token">
            Access token
          </label>
          <input
            id="meta-token"
            className="field__input"
            type="password"
            value={form.accessToken}
            onChange={(event) => {
              field('accessToken', event.target.value);
            }}
            placeholder={
              meta.tokenConfigured
                ? `stored, ending ${meta.tokenLastFour ?? '••••'}`
                : 'paste the System User token'
            }
            autoComplete="new-password"
            spellCheck={false}
            aria-describedby="meta-token-hint"
          />
          <p className="field__hint" id="meta-token-hint">
            A Meta System User token. Write-only: only its last four characters ever come back out.
            Leave it empty to keep the stored one.
          </p>
        </div>

        {idsLocked && (
          <Notice kind="info">
            The ad account, Page and Pixel cannot change while an agent campaign exists. Use “Pause
            all ads”, delete the campaigns in Meta Ads Manager, and then change them here.
          </Notice>
        )}
        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="meta-ad-account">
              Ad account ID
            </label>
            <input
              id="meta-ad-account"
              disabled={idsLocked}
              className="field__input"
              value={form.adAccountId}
              onChange={(event) => {
                field('adAccountId', event.target.value);
              }}
              placeholder="act_123456789"
              autoComplete="off"
              spellCheck={false}
              aria-describedby="meta-ad-account-hint"
            />
            <p className="field__hint" id="meta-ad-account-hint">
              With or without <code>act_</code>; it is stored as <code>act_…</code>.
            </p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="meta-page">
              Facebook Page ID
            </label>
            <input
              id="meta-page"
              disabled={idsLocked}
              className="field__input"
              value={form.pageId}
              onChange={(event) => {
                field('pageId', event.target.value);
              }}
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="meta-instagram">
              Instagram account ID
            </label>
            <input
              id="meta-instagram"
              className="field__input"
              value={form.instagramId}
              onChange={(event) => {
                field('instagramId', event.target.value);
              }}
              placeholder="optional"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="meta-pixel">
              Pixel (dataset) ID
            </label>
            <input
              id="meta-pixel"
              disabled={idsLocked}
              className="field__input"
              value={form.pixelId}
              onChange={(event) => {
                field('pixelId', event.target.value);
              }}
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>

        {!idsLocked && (
          <p className="field__hint">
            Once the agent has a campaign, the ad account, Page and Pixel are locked until you use
            “Pause all ads” and delete its campaigns in Meta Ads Manager.
          </p>
        )}
        <div className="field__actions">
          <button type="submit" className="button button--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Meta Ads settings'}
          </button>
          {meta.tokenConfigured && (
            <button
              type="button"
              className="button button--quiet button--danger"
              onClick={() => {
                setConfirming(true);
              }}
              disabled={removing}
            >
              <Icon name="trash" />
              Remove token
            </button>
          )}
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        title="Remove the Meta access token?"
        confirmLabel="Remove token"
        busyLabel="Removing…"
        danger
        busy={removing}
        onConfirm={removeToken}
        onCancel={() => {
          setConfirming(false);
        }}
      >
        <p>
          marketingchief will no longer be able to read or change your Meta ads until a new token
          is saved. The account, Page, Instagram and Pixel IDs stay as they are.
        </p>
      </ConfirmDialog>

      <MetaAgentSection pageName={meta.pageName} />
    </Panel>
  );
}

/* ------------------------------------------------------- meta ads: agent */

/** How many booked calls a month Meta needs to learn from them directly (server `ads/constants.ts`). */
const MIN_BOOKED_CALLS_FOR_DIRECT_OPTIMISATION_PER_MONTH = 40;
/** What "≈ per day" divides the ceiling by (server `AVERAGE_DAYS_PER_MONTH`). */
const AVERAGE_DAYS_PER_MONTH = 30.4;

/** A typed amount → minor units, or `null` while it is not one. */
function parseAmount(value: string, currency: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const minor = Math.round(Number(trimmed) * minorFactor(currency));
  return minor > 0 ? minor : null;
}

function amountOf(minor: number | null, currency: string): string {
  if (minor === null) return '';
  const factor = minorFactor(currency);
  return factor === 1 ? String(minor) : (minor / factor).toFixed(2).replace(/\.00$/, '');
}

type EventKind = 'none' | 'standard' | 'custom';

interface AgentForm {
  ceiling: string;
  targetCost: string;
  bookedKind: 'standard' | 'custom';
  bookedName: string;
  bookedId: string;
  proxyKind: EventKind;
  proxyName: string;
  proxyId: string;
  landingUrl: string;
  countries: string[];
  /** `''` is "the product brand book's language". */
  language: string;
  creativeEnhancements: boolean;
  product: string;
  dsaBeneficiary: string;
  dsaPayer: string;
  model: AgentModel | '';
  effort: EffortLevel | '';
}

function agentFormOf(agent: AdsAgentSettings): AgentForm {
  const booked = agent.bookedCallEvent;
  const proxy = agent.proxyEvent;
  return {
    ceiling: amountOf(agent.ceilingMinor, agent.currency),
    targetCost: amountOf(agent.targetCostMinor, agent.currency),
    bookedKind: booked.kind,
    bookedName: booked.kind === 'standard' ? booked.name : 'Schedule',
    bookedId: booked.kind === 'custom' ? booked.id : '',
    proxyKind: proxy === null ? 'none' : proxy.kind,
    proxyName: proxy?.kind === 'standard' ? proxy.name : 'Lead',
    proxyId: proxy?.kind === 'custom' ? proxy.id : '',
    landingUrl: agent.landingUrl ?? '',
    countries: agent.countries,
    language: agent.languageSaved ?? '',
    creativeEnhancements: agent.creativeEnhancements,
    product: agent.productRepositoryId ?? '',
    dsaBeneficiary: agent.dsaBeneficiarySaved ?? '',
    dsaPayer: agent.dsaPayerSaved ?? '',
    model: agent.model ?? '',
    effort: agent.effort ?? '',
  };
}

function agentUpdateOf(form: AgentForm): AdsAgentSettingsUpdate {
  const empty = (value: string): string | null => (value.trim() === '' ? null : value.trim());
  return {
    ceiling: empty(form.ceiling),
    targetCost: empty(form.targetCost),
    bookedCallEvent:
      form.bookedKind === 'standard'
        ? { kind: 'standard', name: form.bookedName }
        : { kind: 'custom', id: form.bookedId.trim() },
    proxyEvent:
      form.proxyKind === 'none'
        ? null
        : form.proxyKind === 'standard'
          ? { kind: 'standard', name: form.proxyName }
          : { kind: 'custom', id: form.proxyId.trim() },
    landingUrl: empty(form.landingUrl),
    countries: form.countries,
    language: empty(form.language),
    creativeEnhancements: form.creativeEnhancements,
    productRepositoryId: empty(form.product),
    dsaBeneficiary: empty(form.dsaBeneficiary),
    dsaPayer: empty(form.dsaPayer),
    model: form.model === '' ? null : form.model,
    effort: form.effort === '' ? null : form.effort,
  };
}

/** The EU member states plus Iceland, Liechtenstein and Norway (server `EU_EEA_COUNTRIES`). */
const EU_EEA = new Set(
  'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO'.split(' '),
);

/** What a change does while agent campaigns run, said next to the field. */
const TAKES_EFFECT = {
  budget: 'Takes effect immediately: at the next pacing check and the next review.',
  adset:
    'While agent campaigns run, the server applies a change to the live ad set at the start of the next review and logs it as your decision. This restarts Meta’s learning.',
  newAds:
    'Used for new ads only. Running ads keep their copy and URL, because a running ad cannot be edited.',
} as const;

/**
 * The **Agent** part of Meta Ads (meta-ads-agent US-004): the budget, the
 * target and the market the agent optimises for. Its own form and save, so a
 * change here never re-sends the connection's ids.
 */
function MetaAgentSection({ pageName }: { readonly pageName: string | null }) {
  const toast = useToast();
  const [view, setView] = useState<AdsAgentSettingsView | null>(null);
  const [form, setForm] = useState<AgentForm | null>(null);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([fetchAdsAgentSettings(controller.signal), fetchRepositories(controller.signal)])
      .then(([loaded, repos]) => {
        setView(loaded);
        setForm(agentFormOf(loaded.agent));
        setRepositories(repos);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const field = useCallback(<K extends keyof AgentForm>(key: K, value: AgentForm[K]): void => {
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  }, []);

  if (view === null || form === null) {
    return (
      <section aria-labelledby="meta-agent-title">
        <h3 className="rules-table__title" id="meta-agent-title">
          Agent
        </h3>
        {error === null ? <Skeleton lines={4} /> : <Notice kind="error">{error}</Notice>}
      </section>
    );
  }

  const { agent } = view;
  const currency = agent.currency;
  const ceilingMinor = parseAmount(form.ceiling, currency);
  const targetMinor = parseAmount(form.targetCost, currency);
  const calls = ceilingMinor !== null && targetMinor !== null ? Math.floor(ceilingMinor / targetMinor) : null;
  const optimisesForProxy =
    ceilingMinor !== null &&
    targetMinor !== null &&
    ceilingMinor / targetMinor < MIN_BOOKED_CALLS_FOR_DIRECT_OPTIMISATION_PER_MONTH;
  const dsaRequired = form.countries.some((code) => EU_EEA.has(code));
  const countryName = (code: string): string =>
    view.countryOptions.find((country) => country.code === code)?.name ?? code;
  const live = view.liveCampaigns;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    saveAdsAgentSettings(agentUpdateOf(form))
      .then((saved) => {
        setView(saved);
        setForm(agentFormOf(saved.agent));
        toast.ok(
          saved.liveCampaigns && saved.pendingAdsetChanges !== null
            ? 'Agent settings saved. The ad-set changes are applied at the start of the next review.'
            : 'Agent settings saved.',
        );
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const eventPicker = (
    prefix: 'booked' | 'proxy',
    kind: EventKind,
    name: string,
    id: string,
  ) => (
    <div className="field__pair">
      <select
        id={`meta-agent-${prefix}-kind`}
        className="field__input"
        value={kind}
        onChange={(event) => {
          const next = event.target.value as EventKind;
          if (prefix === 'booked') field('bookedKind', next === 'custom' ? 'custom' : 'standard');
          else field('proxyKind', next);
        }}
        aria-label={prefix === 'booked' ? 'Booked-call event type' : 'Proxy event type'}
      >
        {prefix === 'proxy' && <option value="none">None</option>}
        <option value="standard">Pixel standard event</option>
        <option value="custom">Custom conversion</option>
      </select>
      {kind === 'standard' && (
        <select
          className="field__input field__input--narrow"
          value={name}
          onChange={(event) => {
            field(prefix === 'booked' ? 'bookedName' : 'proxyName', event.target.value);
          }}
          aria-label={prefix === 'booked' ? 'Booked-call standard event' : 'Proxy standard event'}
        >
          {view.standardEvents.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}
      {kind === 'custom' && (
        <input
          className="field__input field__input--narrow"
          value={id}
          onChange={(event) => {
            field(prefix === 'booked' ? 'bookedId' : 'proxyId', event.target.value);
          }}
          placeholder="custom conversion ID"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          aria-label={prefix === 'booked' ? 'Booked-call custom conversion ID' : 'Proxy custom conversion ID'}
        />
      )}
    </div>
  );

  return (
    <section aria-labelledby="meta-agent-title">
      <h3 className="rules-table__title" id="meta-agent-title">
        Agent
      </h3>
      <p className="muted">
        What the agent may spend, what a booked call may cost, and who the ads are for.
        {live ? ' Agent campaigns are running, so each field says when a change takes effect.' : ''}
      </p>
      {agent.missing.length > 0 && (
        <Notice kind="info">
          Still needed before the agent can be enabled: {agent.missing.map(missingLabel).join(', ')}.
        </Notice>
      )}
      {view.pendingAdsetChanges !== null && (
        <Notice kind="warn">
          Waiting for the next review to apply to the live ad set:{' '}
          {view.pendingAdsetChanges.fields.map(adsetLabel).join(', ')}.
        </Notice>
      )}

      <form className="form" onSubmit={submit}>
        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-ceiling">
              Monthly budget ceiling ({currency})
            </label>
            <input
              id="meta-agent-ceiling"
              className="field__input field__input--narrow"
              value={form.ceiling}
              onChange={(event) => {
                field('ceiling', event.target.value);
              }}
              placeholder="300"
              inputMode="decimal"
              autoComplete="off"
              aria-describedby="meta-agent-ceiling-hint"
            />
            <p className="field__hint" id="meta-agent-ceiling-hint">
              The hard limit: the server pauses every agent ad once it is reached.
              {agent.currencyAssumed && ' The currency is assumed until Test connection reads the ad account’s own.'}{' '}
              {TAKES_EFFECT.budget} It cannot go below what the campaigns already spent this month
              {view.spentThisMonthMinor === null ? '' : ` (${formatMoney(view.spentThisMonthMinor, currency)})`}.
            </p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-target">
              Target cost per booked call ({currency})
            </label>
            <input
              id="meta-agent-target"
              className="field__input field__input--narrow"
              value={form.targetCost}
              onChange={(event) => {
                field('targetCost', event.target.value);
              }}
              placeholder="100"
              inputMode="decimal"
              autoComplete="off"
              aria-describedby="meta-agent-target-hint"
            />
            <p className="field__hint" id="meta-agent-target-hint">
              Every agent threshold is measured against this target. It must be lower than the
              ceiling. {TAKES_EFFECT.budget}
            </p>
          </div>
        </div>

        <p className="field__hint" aria-live="polite" id="meta-agent-budget">
          {ceilingMinor === null || targetMinor === null || calls === null
            ? 'Enter the ceiling and the target to see what the budget buys.'
            : `≈ ${formatMoney(Math.round(ceilingMinor / AVERAGE_DAYS_PER_MONTH), currency)} per day, ≈ ${String(calls)} booked call${calls === 1 ? '' : 's'} per month at your target.`}
          {optimisesForProxy &&
            ' That is too few for Meta to learn from, so the agent will optimise for the proxy event.'}
        </p>

        <div className="field__row">
          <div className="field">
            <span className="field__label">Booked-call conversion event</span>
            {eventPicker('booked', form.bookedKind, form.bookedName, form.bookedId)}
            <p className="field__hint">
              The result that counts: how the agent judges the campaign. Built-in: the Pixel’s{' '}
              <code>Schedule</code> event. {TAKES_EFFECT.adset}
            </p>
          </div>
          <div className="field">
            <span className="field__label">
              Proxy event{optimisesForProxy || calls === null ? ' (required)' : ''}
            </span>
            {eventPicker('proxy', form.proxyKind, form.proxyName, form.proxyId)}
            <p className="field__hint">
              What Meta optimises for while booked calls are too few: Meta needs about{' '}
              {MIN_BOOKED_CALLS_FOR_DIRECT_OPTIMISATION_PER_MONTH} conversions a month (10 a week) to
              learn, so below that it needs a more frequent event. The event must already fire on the
              Pixel. A frequent event (e.g. a booking-page visit) gives Meta the most signal; a rarer
              one (e.g. a submitted lead form) is closer to a booked call. {TAKES_EFFECT.adset}
            </p>
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="meta-agent-landing">
            Landing page URL
          </label>
          <input
            id="meta-agent-landing"
            className="field__input"
            type="url"
            value={form.landingUrl}
            onChange={(event) => {
              field('landingUrl', event.target.value);
            }}
            placeholder="https://example.com/book-a-demo"
            autoComplete="off"
            spellCheck={false}
            aria-describedby="meta-agent-landing-hint"
          />
          <p className="field__hint" id="meta-agent-landing-hint">
            Must start with <code>https://</code>. {TAKES_EFFECT.newAds}
          </p>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-countries">
              Countries
            </label>
            <select
              id="meta-agent-countries"
              className="field__input"
              multiple
              size={8}
              value={form.countries}
              onChange={(event) => {
                field(
                  'countries',
                  Array.from(event.target.selectedOptions, (option) => option.value),
                );
              }}
              aria-describedby="meta-agent-countries-hint"
            >
              {view.countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name} ({country.code})
                </option>
              ))}
            </select>
            <p className="field__hint" id="meta-agent-countries-hint">
              {form.countries.length === 0
                ? 'None chosen yet. Hold Ctrl or ⌘ to choose more than one.'
                : `Chosen: ${form.countries.map(countryName).join(', ')}.`}{' '}
              {TAKES_EFFECT.adset}
            </p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-product">
              Product
            </label>
            <select
              id="meta-agent-product"
              className="field__input"
              value={form.product}
              onChange={(event) => {
                field('product', event.target.value);
              }}
              aria-describedby="meta-agent-product-hint"
            >
              <option value="">Choose a repository</option>
              {repositories.map((repository) => (
                <option key={repository.id} value={repository.id}>
                  {repository.name}
                </option>
              ))}
            </select>
            <p className="field__hint" id="meta-agent-product-hint">
              The repository whose brand book and repository profile describe the product being
              advertised. {TAKES_EFFECT.newAds}
            </p>

            <label className="field__label" htmlFor="meta-agent-language">
              Ad language
            </label>
            <select
              id="meta-agent-language"
              className="field__input"
              value={form.language}
              onChange={(event) => {
                field('language', event.target.value);
              }}
              aria-describedby="meta-agent-language-hint"
            >
              <option value="">
                Brand book language
                {agent.languageSaved === null ? ` (${languageLabel(agent.language)})` : ''}
              </option>
              {(form.language === '' || DEFAULT_LANGUAGE_CHOICES.includes(form.language)
                ? DEFAULT_LANGUAGE_CHOICES
                : [form.language, ...DEFAULT_LANGUAGE_CHOICES]
              ).map((option) => (
                <option key={option} value={option}>
                  {languageLabel(option)}
                </option>
              ))}
            </select>
            <p className="field__hint" id="meta-agent-language-hint">
              {TAKES_EFFECT.newAds}
            </p>
          </div>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-dsa-beneficiary">
              DSA beneficiary{dsaRequired ? ' (required)' : ''}
            </label>
            <input
              id="meta-agent-dsa-beneficiary"
              className="field__input"
              value={form.dsaBeneficiary}
              onChange={(event) => {
                field('dsaBeneficiary', event.target.value);
              }}
              placeholder={pageName ?? 'the Page name, after Test connection'}
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-dsa-payer">
              DSA payer{dsaRequired ? ' (required)' : ''}
            </label>
            <input
              id="meta-agent-dsa-payer"
              className="field__input"
              value={form.dsaPayer}
              onChange={(event) => {
                field('dsaPayer', event.target.value);
              }}
              placeholder={pageName ?? 'the Page name, after Test connection'}
              autoComplete="off"
            />
          </div>
        </div>
        <p className="field__hint">
          EU ad transparency (DSA): who benefits from and who pays for ads shown in the EU/EEA.
          Required when you target an EU/EEA country; empty uses the Page name. {TAKES_EFFECT.adset}
        </p>

        <ul className="choices">
          <li className="choice">
            <label className="choice__label">
              <input
                type="checkbox"
                checked={form.creativeEnhancements}
                onChange={() => {
                  field('creativeEnhancements', !form.creativeEnhancements);
                }}
              />
              <span className="choice__main">
                <span className="choice__title">Allow Meta Advantage+ creative enhancements</span>
                <span className="choice__summary">
                  Lets Meta adjust brightness, crops, music and text per placement. On by default.
                </span>
              </span>
            </label>
          </li>
        </ul>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-model">
              Ads agent model
            </label>
            <select
              id="meta-agent-model"
              className="field__input"
              value={form.model}
              onChange={(event) => {
                field('model', event.target.value as AgentModel | '');
              }}
            >
              <option value="">Let Claude Code choose</option>
              {AGENT_MODELS.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="meta-agent-effort">
              Ads agent effort
            </label>
            <select
              id="meta-agent-effort"
              className="field__input"
              value={form.effort}
              onChange={(event) => {
                field('effort', event.target.value as EffortLevel | '');
              }}
              aria-describedby="meta-agent-effort-hint"
            >
              <option value="">Let Claude Code choose</option>
              {EFFORT_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
            <p className="field__hint" id="meta-agent-effort-hint">
              The review twice a day. Built-in: high.
            </p>
          </div>
        </div>

        {error !== null && <Notice kind="error">{error}</Notice>}
        <div className="field__actions">
          <button type="submit" className="button button--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save agent settings'}
          </button>
        </div>
      </form>
    </section>
  );
}

const MISSING_LABEL: Record<string, string> = {
  ceiling: 'the monthly ceiling',
  targetCost: 'the target cost per booked call',
  proxyEvent: 'a proxy event',
  landingUrl: 'the landing page',
  countries: 'countries',
  product: 'the product',
  dsaBeneficiary: 'the DSA beneficiary',
  dsaPayer: 'the DSA payer',
};

function missingLabel(field: string): string {
  return MISSING_LABEL[field] ?? field;
}

const ADSET_LABEL: Record<string, string> = {
  countries: 'countries',
  dsaBeneficiary: 'DSA beneficiary',
  dsaPayer: 'DSA payer',
  bookedCallEvent: 'booked-call event',
  proxyEvent: 'proxy event',
};

function adsetLabel(field: string): string {
  return ADSET_LABEL[field] ?? field;
}

/* --------------------------------------------------------------- providers */

/**
 * **Providers** (PLAN §14.3, US-062).
 *
 * One card per provider, and every card is the same four things: a switch, a
 * write-only key, the defaults it needs, and **Validate**. The panel never
 * shows a key back — only its last four — so a browser left open on this page
 * is not a leak and a screenshot of it is safe to paste into a thread.
 *
 * **Validate** is the point of the card. It is the only thing that can answer
 * "is this actually going to work", it fills the voice and model pickers from
 * the provider's own API, and it brings back the credit balance — one pool
 * across voice, music and effects — so the operator sees what the skills see in
 * `mc budget`.
 */
function ProvidersPanel() {
  const [providers, setProviders] = useState<ProviderView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchProviders(controller.signal)
      .then(setProviders)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const replace = useCallback((saved: ProviderView): void => {
    setProviders((current) =>
      current === null ? current : current.map((one) => (one.id === saved.id ? saved : one)),
    );
  }, []);

  if (providers === null) {
    return (
      <Panel
        id="providers"
        title="Providers"
        icon="key"
        collapsible
        defaultOpen={false}
        summary={pendingSummary(error)}
      >
        {error === null ? <Skeleton lines={5} /> : <Notice kind="error">{error}</Notice>}
      </Panel>
    );
  }

  const usable = providers.filter((one) => one.settings.usable).length;

  return (
    <Panel
      id="providers"
      title="Providers"
      icon="key"
      collapsible
      defaultOpen={false}
      meta={usable === 0 && <Badge tone="danger">action needed</Badge>}
      summary={`${String(usable)} of ${String(providers.length)} usable`}
    >
      <p className="muted">
        <Icon name="info" /> A provider that is switched off is absent from{' '}
        <code>mc budget</code> altogether, so a skill cannot even read a price for a call this
        gateway would refuse — it falls back to the free local voice and libraries instead. Keys
        never leave this server: containers call the gateway, never the provider.
      </p>
      {providers.map((provider) => (
        <ProviderCard key={provider.id} provider={provider} onSaved={replace} />
      ))}
    </Panel>
  );
}

/** The key field is empty unless one was typed; empty means "keep the stored one". */
interface ProviderForm {
  enabled: boolean;
  apiKey: string;
  defaults: Record<string, string>;
}

function providerFormOf(provider: ProviderView): ProviderForm {
  const defaults: Record<string, string> = {};
  for (const field of provider.fields) defaults[field.key] = field.value;
  return { enabled: provider.settings.enabled, apiKey: '', defaults };
}

function ProviderCard({
  provider,
  onSaved,
}: {
  readonly provider: ProviderView;
  readonly onSaved: (saved: ProviderView) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<ProviderForm>(() => providerFormOf(provider));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ProviderValidation | null>(null);

  const settings = provider.settings;
  const shown = validation ?? provider.snapshot.validation;
  const credits = provider.snapshot.credits;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    const update: ProviderUpdate = {
      enabled: form.enabled,
      defaults: form.defaults,
      // Never sent unless it was typed: there is no value that means "the key
      // you already have", so an untouched field is simply absent.
      ...(form.apiKey === '' ? {} : { apiKey: form.apiKey }),
    };
    saveProvider(provider.id, update)
      .then((saved) => {
        onSaved(saved);
        setForm(providerFormOf(saved));
        toast.ok(`${provider.label} saved. It applies to the next call.`);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const runValidate = (): void => {
    if (validating) return;
    setValidating(true);
    setError(null);
    validateProvider(provider.id)
      .then((result) => {
        setValidation(result.validation);
        onSaved(result.provider);
        setForm(providerFormOf(result.provider));
        if (result.validation.ok) toast.ok(result.validation.message);
        else toast.error(result.validation.message);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setValidating(false);
      });
  };

  return (
    <Panel
      id={`provider-${provider.id}`}
      title={provider.label}
      icon="unmute"
      meta={
        <Badge tone={settings.usable ? 'done' : settings.keyConfigured ? 'wait' : 'neutral'}>
          {settings.usable ? 'on' : settings.keyConfigured ? 'off' : 'no key'}
        </Badge>
      }
      actions={
        <button
          type="button"
          className="button"
          onClick={runValidate}
          disabled={validating || !settings.keyConfigured}
          title={
            settings.keyConfigured
              ? 'Checks the key, the account, the default voice and the default model'
              : 'Save an API key first'
          }
        >
          <Icon name="pulse" />
          {validating ? 'Validating…' : 'Validate'}
        </button>
      }
    >
      <p className="muted">{provider.summary}</p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {shown !== null && <ProviderChecks validation={shown} />}
      {credits !== null && <ProviderCreditsView credits={credits} />}

      <form className="form" onSubmit={submit}>
        <div className="field field--checkbox">
          <label className="field__label" htmlFor={`${provider.id}-enabled`}>
            <input
              id={`${provider.id}-enabled`}
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => {
                setForm((current) => ({ ...current, enabled: event.target.checked }));
              }}
            />{' '}
            Use {provider.label}
          </label>
          <p className="field__hint">
            Switched off, its prices and its credits are absent from <code>mc budget</code> and its{' '}
            {provider.operations.map((one) => `mc ${one}`).join(', ')} route answers as though it
            did not exist.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={`${provider.id}-key`}>
            API key
          </label>
          <input
            id={`${provider.id}-key`}
            className="field__input"
            type="password"
            autoComplete="off"
            placeholder={
              settings.keyLastFour === null ? 'Not set' : `Stored, ending ${settings.keyLastFour}`
            }
            value={form.apiKey}
            onChange={(event) => {
              setForm((current) => ({ ...current, apiKey: event.target.value }));
            }}
            aria-describedby={`${provider.id}-key-hint`}
          />
          <p className="field__hint" id={`${provider.id}-key-hint`}>
            {provider.keyHelp}{' '}
            <a href={provider.keyUrl} target="_blank" rel="noreferrer">
              Where to get one
            </a>
            . Leave empty to keep the stored key.
          </p>
        </div>

        {provider.fields.map((field) => (
          <div className="field" key={field.key}>
            <label className="field__label" htmlFor={`${provider.id}-${field.key}`}>
              {field.label}
            </label>
            {field.options.length === 0 ? (
              <input
                id={`${provider.id}-${field.key}`}
                className="field__input"
                value={form.defaults[field.key] ?? ''}
                placeholder={field.fallback === '' ? 'Validate to list the choices' : field.fallback}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((current) => ({
                    ...current,
                    defaults: { ...current.defaults, [field.key]: value },
                  }));
                }}
              />
            ) : (
              <select
                id={`${provider.id}-${field.key}`}
                className="field__input"
                value={form.defaults[field.key] ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((current) => ({
                    ...current,
                    defaults: { ...current.defaults, [field.key]: value },
                  }));
                }}
              >
                <option value="">No default</option>
                {field.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                    {option.detail === null ? '' : ` — ${option.detail}`}
                  </option>
                ))}
              </select>
            )}
            <p className="field__hint">{field.help}</p>
          </div>
        ))}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : `Save ${provider.label}`}
          </button>
          <span className="muted">
            {provider.prices
              .map((price) => `${price.operation}: $${price.usdPerUnit.toFixed(4)} per ${price.unit}`)
              .join(' · ')}
          </span>
        </div>
      </form>
    </Panel>
  );
}

/** The named checks of the last **Validate**, in the order they were run. */
function ProviderChecks({ validation }: { readonly validation: ProviderValidation }) {
  return (
    <>
      <Notice kind={validation.ok ? 'ok' : 'error'}>{validation.message}</Notice>
      <ol className="stages" aria-label="Provider checks">
        {validation.checks.map((check) => (
          <li className={`stage ${CHECK_TONE[check.state]}`.trim()} key={check.name}>
            <span className="stage__marker">
              {check.state === 'passed' ? (
                <Icon name="check" />
              ) : check.state === 'failed' ? (
                <Icon name="x" />
              ) : (
                <Icon name="clock" />
              )}
            </span>
            <span className="stage__text">
              <span className="stage__label">{check.name}</span>
              <span className="stage__detail">{check.detail}</span>
              {check.error !== null && (
                <span className="output output--wrap">{check.error}</span>
              )}
            </span>
          </li>
        ))}
      </ol>
      <p className="muted">Last validated {localTime(validation.at)}.</p>
    </>
  );
}

/**
 * The credit balance, as one pool.
 *
 * Voice, music and sound effects all come out of the same figure and this
 * gateway enforces no split between them — so the remaining number is stated
 * once, and the breakdown underneath is *this install's own* count of where its
 * credits went, not an allowance per category.
 */
function ProviderCreditsView({ credits }: { readonly credits: ProviderCredits }) {
  const estimate = credits.gatewayEstimate;
  // The breakdown names only what this provider actually sells: an image
  // provider has no voice line, and a zero beside "music" on a card that
  // cannot generate music reads as a broken feature rather than as no spend.
  const spentOn = (
    [
      [estimate.voiceCredits, 'voice'],
      [estimate.musicCredits, 'music'],
      [estimate.effectCredits, 'sound effects'],
      [estimate.imageCredits ?? 0, 'images'],
    ] as const
  ).filter(([amount]) => amount > 0);
  return (
    <div className="facts">
      <p className="muted">
        <Icon name="graph" /> Tier {credits.tier ?? 'unknown'} —{' '}
        {credits.usedCredits === null || credits.limitCredits === null
          ? 'no credit figures'
          : `${credits.usedCredits.toLocaleString()} of ${credits.limitCredits.toLocaleString()} credits used`}
        {credits.remainingCredits === null
          ? ''
          : `, ${credits.remainingCredits.toLocaleString()} left`}
        {credits.resetsAt === null ? '' : `, resetting ${localTime(credits.resetsAt)}`}.
      </p>
      <p className="muted">
        <Icon name="list" /> This install spent {estimate.totalCredits.toLocaleString()} of them
        over {String(estimate.calls)} calls this cycle by its own count
        {spentOn.length === 0
          ? ''
          : `: ${spentOn.map(([amount, what]) => `${amount.toLocaleString()} on ${what}`).join(', ')}`}
        . {credits.note}
      </p>
      <p className="muted">Read {localTime(credits.at)}.</p>
    </div>
  );
}

/* --------------------------------------------- video generation (US-084) */

/**
 * **Video generation** (PLAN §14.3, FR-46, decision D1, US-084).
 *
 * The sub-panel of Providers that makes the video model a choice rather than a
 * constant: OpenRouter's own catalogue, read on the server at most once an
 * hour, with what a second and a whole clip cost on each model, the
 * resolutions and durations it takes, and whether it does image-to-video and
 * its own sound. Picking one writes the same four provider rows the OpenRouter
 * card holds — there is one place a default model is stored, and this panel is
 * a better way of editing it, not a second copy of it.
 *
 * The warning is the reason the four settings are shown *beside* the
 * catalogue rather than only on the card: a default model and a default
 * resolution are saved separately and nothing stops them disagreeing. A model
 * that does not list the resolution asked for answers at its own nearest size
 * and bills for it, which is a surprise best found here rather than in a
 * finished video.
 */
function VideoPanel() {
  const toast = useToast();
  const [view, setView] = useState<VideoCatalogueView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<VideoForm>(EMPTY_VIDEO_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchVideoModels(controller.signal)
      .then((loaded) => {
        setView(loaded);
        setForm(videoFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  if (view === null) {
    return (
      <Panel
        id="video"
        title="Video generation"
        icon="unmute"
        collapsible
        defaultOpen={false}
        summary={pendingSummary(error)}
      >
        {error === null ? <Skeleton lines={4} /> : <Notice kind="error">{error}</Notice>}
      </Panel>
    );
  }

  const chosen = view.models.find((one) => one.model === form.model) ?? null;
  // Computed here as well as on the server, because the operator is entitled
  // to the answer while they are still choosing rather than after they save.
  const mismatch =
    chosen === null || chosen.resolutions.length === 0
      ? false
      : !chosen.resolutions.some((one) => sameResolution(one, form.resolution));

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    saveProvider('openrouter', {
      defaults: {
        video_model: form.model,
        video_resolution: form.resolution,
        max_clips: form.maxClips,
        max_clip_seconds: form.maxClipSeconds,
      },
    })
      .then(async () => fetchVideoModels())
      .then((loaded) => {
        setView(loaded);
        setForm(videoFormOf(loaded));
        toast.ok('Video settings saved. They apply to the next clip.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Panel
      id="video"
      title="Video generation"
      icon="unmute"
      collapsible
      defaultOpen={false}
      summary={view.available ? (view.listed?.name ?? view.model) : 'off'}
    >
      <p className="muted">
        <Icon name="info" /> {view.note}
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {view.warnings.map((warning) => (
        <Notice kind="warn" key={warning}>
          {warning}
        </Notice>
      ))}
      {mismatch && chosen !== null && (
        <Notice kind="warn">
          {chosen.name} does not produce {form.resolution}. It lists{' '}
          {chosen.resolutions.join(', ')} — a clip asked for at {form.resolution} comes back at
          the model&rsquo;s own nearest size and is billed at its own price either way.
        </Notice>
      )}

      <form className="form" onSubmit={submit}>
        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="video-model">
              Default video model
            </label>
            <select
              id="video-model"
              className="field__input"
              value={form.model}
              onChange={(event) => {
                setForm({ ...form, model: event.target.value });
              }}
              aria-describedby="video-model-hint"
            >
              {view.models.every((one) => one.model !== form.model) && (
                <option value={form.model}>{form.model} — not in the catalogue</option>
              )}
              {view.models.map((one) => (
                <option key={one.model} value={one.model}>
                  {one.name} — {priceLabel(one)}
                </option>
              ))}
            </select>
            <p className="field__hint" id="video-model-hint">
              What a <code>clip</code> step generates on when its plan names none. A model named
              on the call itself wins; the caps below never move.
            </p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="video-resolution">
              Default clip resolution
            </label>
            <input
              id="video-resolution"
              className="field__input"
              value={form.resolution}
              onChange={(event) => {
                setForm({ ...form, resolution: event.target.value });
              }}
              aria-describedby="video-resolution-hint"
            />
            <p className="field__hint" id="video-resolution-hint">
              <code>720p</code>, or a size as <code>widthxheight</code>.{' '}
              {chosen === null || chosen.resolutions.length === 0
                ? 'The catalogue does not say which sizes this model produces.'
                : `${chosen.name} produces ${chosen.resolutions.join(', ')}.`}
            </p>
          </div>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="video-max-clips">
              Clips per video
            </label>
            <input
              id="video-max-clips"
              className="field__input"
              inputMode="numeric"
              value={form.maxClips}
              onChange={(event) => {
                setForm({ ...form, maxClips: event.target.value });
              }}
              aria-describedby="video-max-clips-hint"
            />
            <p className="field__hint" id="video-max-clips-hint">
              How many clips one content may generate before <code>mc video generate</code>{' '}
              refuses. A replayed clip costs nothing and does not count.
            </p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="video-max-seconds">
              Seconds per clip
            </label>
            <input
              id="video-max-seconds"
              className="field__input"
              inputMode="numeric"
              value={form.maxClipSeconds}
              onChange={(event) => {
                setForm({ ...form, maxClipSeconds: event.target.value });
              }}
              aria-describedby="video-max-seconds-hint"
            />
            <p className="field__hint" id="video-max-seconds-hint">
              The longest one generated clip may be. B-roll longer than that is a cut, not a
              clip.
            </p>
          </div>
        </div>

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save video settings'}
          </button>
          <span className="muted">
            {view.usdPerSecond === null
              ? 'This model is not priced per second here.'
              : `About ${perSecond(view.usdPerSecond * view.defaultSeconds)} for a ${String(
                  view.defaultSeconds,
                )}-second clip at ${view.resolution}, ${perSecond(view.usdPerSecond)} a second.`}
          </span>
        </div>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th scope="col">Model</th>
            <th scope="col">A second</th>
            <th scope="col">Resolutions</th>
            <th scope="col">Durations</th>
            <th scope="col">Image-to-video</th>
            <th scope="col">Audio</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {view.models.map((one) => (
            <tr key={one.model}>
              <td>
                <span className="mono">{one.model}</span>
                {one.model === view.model && <> <Badge tone="done">default</Badge></>}
              </td>
              <td className="num">{priceLabel(one)}</td>
              <td>{one.resolutions.length === 0 ? '—' : one.resolutions.join(', ')}</td>
              <td>{one.durations.length === 0 ? '—' : `${one.durations.join(', ')} s`}</td>
              <td>{yesNo(one.imageToVideo)}</td>
              <td>{yesNo(one.audio)}</td>
              <td>
                <button
                  type="button"
                  className="button"
                  disabled={one.model === form.model}
                  onClick={() => {
                    setForm({ ...form, model: one.model });
                  }}
                >
                  Use
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        <Icon name="list" /> {describeCatalogue(view)} The list is re-read at most every{' '}
        {String(Math.round(view.ttlSeconds / 60))} minutes, so opening this page does not call
        OpenRouter.
      </p>

      <AnimationSection view={view} onCatalogue={setView} />
      <TalkSection view={view} onCatalogue={setView} />
      <LibraryEmbeddingSection />
    </Panel>
  );
}

/**
 * **Image animation** (US-002): whether an `animate` step may turn a still
 * into a clip, on which model, and how much of it one content may buy.
 *
 * These five are app settings, saved through `PUT /api/settings` rather than
 * onto the OpenRouter card like the four above, so the section is its own
 * form. The picker lists only what the catalogue says takes a first frame, and
 * the warning under it is the server's own — the same sentence
 * `mc animate plan` gives the agent — re-read after every save.
 */
function AnimationSection({
  view,
  onCatalogue,
}: {
  view: VideoCatalogueView;
  onCatalogue: (view: VideoCatalogueView) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<AnimationForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setForm(animationFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  if (form === null) {
    return (
      <section aria-labelledby="animation-title">
        <h3 className="rules-table__title" id="animation-title">
          Image animation
        </h3>
        {error === null ? <Skeleton lines={3} /> : <Notice kind="error">{error}</Notice>}
      </section>
    );
  }

  const offered = view.models.filter((one) => one.imageToVideo === true);
  const saved = view.animation;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    saveSettings({
      animationEnabled: form.enabled,
      animationModel: form.model,
      maxAnimationsPerContent: Number(form.maxPerContent),
      maxAnimationSeconds: Number(form.maxSeconds),
      maxAnimationAttempts: Number(form.maxAttempts),
    })
      .then(async (stored) => {
        setForm(animationFormOf(stored));
        onCatalogue(await fetchVideoModels());
        toast.ok('Animation settings saved. They apply to the next call.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <section aria-labelledby="animation-title">
      <h3 className="rules-table__title" id="animation-title">
        Image animation
      </h3>
      <p className="muted">
        Turns a still — a logo, a mascot, a screenshot — into a short clip with an image-to-video
        model. Every attempt is billed, re-rolls included.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {saved.warnings.map((warning) => (
        <Notice kind="warn" key={warning}>
          {warning}
        </Notice>
      ))}

      <form className="form" onSubmit={submit}>
        <div className="field">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => {
                setForm({ ...form, enabled: event.target.checked });
              }}
            />
            Allow images to be animated
          </label>
          <p className="field__hint">
            Off, <code>mc animate</code> refuses and an <code>animate</code> step falls back to the
            still with HTML/CSS motion.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="animation-model">
            Animation model
          </label>
          <select
            id="animation-model"
            className="field__input"
            value={form.model}
            onChange={(event) => {
              setForm({ ...form, model: event.target.value });
            }}
            aria-describedby="animation-model-hint"
          >
            {offered.every((one) => one.model !== form.model) && (
              <option value={form.model}>{form.model} — not listed with image-to-video</option>
            )}
            {offered.map((one) => (
              <option key={one.model} value={one.model}>
                {one.name} — {priceLabel(one)}
              </option>
            ))}
          </select>
          <p className="field__hint" id="animation-model-hint">
            Only models OpenRouter lists with image-to-video, with what a second costs on each.
          </p>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="animation-max-per-content">
              Animations per content
            </label>
            <input
              id="animation-max-per-content"
              className="field__input"
              inputMode="numeric"
              value={form.maxPerContent}
              onChange={(event) => {
                setForm({ ...form, maxPerContent: event.target.value });
              }}
            />
            <p className="field__hint">Successful clips only; a re-roll does not count here.</p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="animation-max-seconds">
              Seconds per animation
            </label>
            <input
              id="animation-max-seconds"
              className="field__input"
              inputMode="numeric"
              value={form.maxSeconds}
              onChange={(event) => {
                setForm({ ...form, maxSeconds: event.target.value });
              }}
            />
            <p className="field__hint">At most 10.</p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="animation-max-attempts">
              Attempts per animation
            </label>
            <input
              id="animation-max-attempts"
              className="field__input"
              inputMode="numeric"
              value={form.maxAttempts}
              onChange={(event) => {
                setForm({ ...form, maxAttempts: event.target.value });
              }}
            />
            <p className="field__hint">The first try included; each one is billed.</p>
          </div>
        </div>

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save animation settings'}
          </button>
          <span className="muted">
            {saved.available
              ? saved.usdPerSecond === null
                ? `Animating on ${saved.model}.`
                : `About ${perSecond(saved.usdPerSecond * saved.maxSeconds)} for a ${String(
                    saved.maxSeconds,
                  )}-second animation, ${perSecond(saved.usdPerSecond)} a second.`
              : (saved.reason ?? '')}
          </span>
        </div>
      </form>
    </section>
  );
}

/**
 * **Talking head**: whether a `talk` step may lip-sync a photo of a person to
 * the narration, on which model, and how much of it one content may buy. Its
 * own form, like the animation section, because it is its own money: a talking
 * head is billed per second of speech.
 */
function TalkSection({
  view,
  onCatalogue,
}: {
  view: VideoCatalogueView;
  onCatalogue: (view: VideoCatalogueView) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<TalkForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setForm(talkFormOf(loaded));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  if (form === null) {
    return (
      <section aria-labelledby="talk-title">
        <h3 className="rules-table__title" id="talk-title">
          Talking head
        </h3>
        {error === null ? <Skeleton lines={3} /> : <Notice kind="error">{error}</Notice>}
      </section>
    );
  }

  const saved = view.talk;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    saveSettings({
      talkEnabled: form.enabled,
      talkModel: form.model,
      maxTalksPerContent: Number(form.maxPerContent),
      maxTalkSeconds: Number(form.maxSeconds),
      maxTalkAttempts: Number(form.maxAttempts),
    })
      .then(async (stored) => {
        setForm(talkFormOf(stored));
        onCatalogue(await fetchVideoModels());
        toast.ok('Talking-head settings saved. They apply to the next call.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <section aria-labelledby="talk-title">
      <h3 className="rules-table__title" id="talk-title">
        Talking head
      </h3>
      <p className="muted">
        Makes a person in a photo speak the narration, lip-synced — the presenter or the person
        on the beach a brief asks for. The narration is handed to the model as a link to your
        bucket, so storage has to be reachable from the internet over HTTPS. Every attempt is
        billed per second of speech.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <form className="form" onSubmit={submit}>
        <div className="field">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => {
                setForm({ ...form, enabled: event.target.checked });
              }}
            />
            Allow a person to talk on camera
          </label>
          <p className="field__hint">
            Off, <code>mc talk</code> refuses and a <code>talk</code> step falls back to the photo
            with the narration over it.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="talk-model">
            Talking-head model
          </label>
          <input
            id="talk-model"
            className="field__input"
            value={form.model}
            onChange={(event) => {
              setForm({ ...form, model: event.target.value });
            }}
            aria-describedby="talk-model-hint"
          />
          <p className="field__hint" id="talk-model-hint">
            An OpenRouter model that lip-syncs a photo to a supplied audio track, such as{' '}
            <code>heygen/avatar-iv</code>.
          </p>
        </div>

        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor="talk-max-per-content">
              Talking heads per content
            </label>
            <input
              id="talk-max-per-content"
              className="field__input"
              inputMode="numeric"
              value={form.maxPerContent}
              onChange={(event) => {
                setForm({ ...form, maxPerContent: event.target.value });
              }}
            />
            <p className="field__hint">Successful clips only; a re-roll does not count here.</p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="talk-max-seconds">
              Seconds of speech per clip
            </label>
            <input
              id="talk-max-seconds"
              className="field__input"
              inputMode="numeric"
              value={form.maxSeconds}
              onChange={(event) => {
                setForm({ ...form, maxSeconds: event.target.value });
              }}
            />
            <p className="field__hint">The clip is as long as its narration. At most 120.</p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="talk-max-attempts">
              Attempts per clip
            </label>
            <input
              id="talk-max-attempts"
              className="field__input"
              inputMode="numeric"
              value={form.maxAttempts}
              onChange={(event) => {
                setForm({ ...form, maxAttempts: event.target.value });
              }}
            />
            <p className="field__hint">The first try included; each one is billed.</p>
          </div>
        </div>

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save talking-head settings'}
          </button>
          <span className="muted">
            {saved.available
              ? saved.usdPerSecond === null
                ? `Lip-syncing on ${saved.model}.`
                : `About ${perSecond(saved.usdPerSecond * 20)} for 20 seconds of speech, ${perSecond(
                    saved.usdPerSecond,
                  )} a second.`
              : (saved.reason ?? '')}
          </span>
        </div>
      </form>
    </section>
  );
}

interface TalkForm {
  enabled: boolean;
  model: string;
  maxPerContent: string;
  maxSeconds: string;
  maxAttempts: string;
}

function talkFormOf(settings: Settings): TalkForm {
  return {
    enabled: settings.talkEnabled,
    model: settings.talkModel,
    maxPerContent: String(settings.maxTalksPerContent),
    maxSeconds: String(settings.maxTalkSeconds),
    maxAttempts: String(settings.maxTalkAttempts),
  };
}

/**
 * **Library search** (US-018): the OpenRouter model every library item's
 * words are embedded with, so search can match by meaning.
 *
 * An app setting like the animation section's, so its own small form. The
 * last failed embedding call is shown verbatim beside the field: a model the
 * account cannot use is only fixable by whoever reads that sentence, and until
 * then search quietly falls back to keywords.
 */
function LibraryEmbeddingSection() {
  const toast = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [model, setModel] = useState('');
  const [describerModel, setDescriberModel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingDescriber, setSavingDescriber] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller.signal)
      .then((loaded) => {
        setSettings(loaded);
        setModel(loaded.libraryEmbeddingModel);
        setDescriberModel(loaded.libraryDescriberModel);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  if (settings === null) {
    return (
      <section aria-labelledby="library-embedding-title">
        <h3 className="rules-table__title" id="library-embedding-title">
          Library search
        </h3>
        {error === null ? <Skeleton lines={2} /> : <Notice kind="error">{error}</Notice>}
      </section>
    );
  }

  const failure = settings.libraryEmbeddingError;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    saveSettings({ libraryEmbeddingModel: model })
      .then((stored) => {
        setSettings(stored);
        setModel(stored.libraryEmbeddingModel);
        toast.ok('Embedding model saved. The library is re-embedded in the background.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const submitDescriber = (event: FormEvent): void => {
    event.preventDefault();
    if (savingDescriber) return;
    setSavingDescriber(true);
    setError(null);
    saveSettings({ libraryDescriberModel: describerModel })
      .then((stored) => {
        setSettings(stored);
        setDescriberModel(stored.libraryDescriberModel);
        toast.ok('Describer model saved.');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setSavingDescriber(false);
      });
  };

  return (
    <section aria-labelledby="library-embedding-title">
      <h3 className="rules-table__title" id="library-embedding-title">
        Library search
      </h3>
      <p className="muted">
        Every library item's title, description and tags are embedded so search can find them by
        meaning. Each call is billed at a fraction of a cent; without a working model, search
        matches keywords only.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <form className="form" onSubmit={submit}>
        <div className="field">
          <label className="field__label" htmlFor="library-embedding-model">
            Library embedding model
          </label>
          <input
            id="library-embedding-model"
            className="field__input"
            value={model}
            spellCheck={false}
            onChange={(event) => {
              setModel(event.target.value);
            }}
            aria-describedby="library-embedding-model-hint"
          />
          <p className="field__hint" id="library-embedding-model-hint">
            Any OpenRouter embedding model id. Changing it re-embeds the library in the
            background, a hundred items a minute.
          </p>
          {failure !== null && (
            <Notice kind="error">
              Last embedding error ({failure.model}, {localTime(failure.at)}): {failure.message}
            </Notice>
          )}
        </div>
        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save embedding model'}
          </button>
        </div>
      </form>

      <form className="form" onSubmit={submitDescriber}>
        <div className="field">
          <label className="field__label" htmlFor="library-describer-model">
            Library describer model
          </label>
          <input
            id="library-describer-model"
            className="field__input"
            value={describerModel}
            spellCheck={false}
            onChange={(event) => {
              setDescriberModel(event.target.value);
            }}
            aria-describedby="library-describer-model-hint"
          />
          <p className="field__hint" id="library-describer-model-hint">
            The vision model that writes titles, descriptions and tags for library items (Suggest
            description, and <code>mc-admin library backfill</code>). Billed per call on OpenRouter.
          </p>
        </div>
        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={savingDescriber}>
            {savingDescriber ? 'Saving…' : 'Save describer model'}
          </button>
        </div>
      </form>
    </section>
  );
}

interface AnimationForm {
  enabled: boolean;
  model: string;
  maxPerContent: string;
  maxSeconds: string;
  maxAttempts: string;
}

function animationFormOf(settings: Settings): AnimationForm {
  return {
    enabled: settings.animationEnabled,
    model: settings.animationModel,
    maxPerContent: String(settings.maxAnimationsPerContent),
    maxSeconds: String(settings.maxAnimationSeconds),
    maxAttempts: String(settings.maxAnimationAttempts),
  };
}

interface VideoForm {
  model: string;
  resolution: string;
  maxClips: string;
  maxClipSeconds: string;
}

const EMPTY_VIDEO_FORM: VideoForm = {
  model: '',
  resolution: '',
  maxClips: '',
  maxClipSeconds: '',
};

function videoFormOf(view: VideoCatalogueView): VideoForm {
  return {
    model: view.model,
    resolution: view.resolution,
    maxClips: String(view.maxClips),
    maxClipSeconds: String(view.maxSecondsPerClip),
  };
}

/** Where the list came from, said plainly — a stale list is still a list. */
function describeCatalogue(view: VideoCatalogueView): string {
  if (view.source === 'builtin') return 'OpenRouter’s catalogue has never been read here.';
  const read = view.at === null ? 'never' : localTime(view.at);
  return view.source === 'stale'
    ? `OpenRouter could not be reached; this is the list last read, ${read}.`
    : `Read from OpenRouter ${read}.`;
}

/** The price of one model, as a figure or as a range. */
function priceLabel(model: VideoModelListing): string {
  if (model.usdPerSecond === null) return `per ${model.priceUnit}`;
  return model.usdPerSecondMax !== null && model.usdPerSecondMax > model.usdPerSecond
    ? `${perSecond(model.usdPerSecond)}–${perSecond(model.usdPerSecondMax)}`
    : perSecond(model.usdPerSecond);
}

/** Money to four decimals with the trailing zeros cut: a second is cents. */
function perSecond(usd: number): string {
  return `$${usd.toFixed(4).replace(/(\.\d{2}\d*?)0+$/, '$1')}`;
}

/** A catalogue that does not say is a dash, never a "no". */
function yesNo(value: boolean | null): string {
  return value === null ? '—' : value ? 'yes' : 'no';
}

/** `1280x720` and `720p` are the same size asked for two ways. */
function sameResolution(one: string, other: string): boolean {
  return normalisedResolution(one) === normalisedResolution(other);
}

function normalisedResolution(value: string): string {
  const trimmed = value.trim();
  const size = /^(\d+)\s*[x×]\s*(\d+)$/i.exec(trimmed);
  if (size?.[2] !== undefined) return `${size[2]}p`;
  const named = /^(\d+)\s*([pk])$/i.exec(trimmed);
  if (named?.[1] === undefined || named[2] === undefined) return trimmed;
  return `${named[1]}${named[2].toLowerCase() === 'k' ? 'K' : 'p'}`;
}
